import React, { useState, useEffect } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import './CommonStyles.css';
import './Products.css';
import './CRM.css';
import {
  Users, UserCheck, TrendingUp, Calendar, ChevronDown,
  Download, FileSpreadsheet, FileText, Printer, Phone,
  Star, BarChart2, Settings, Mail, Tag, Heart, Megaphone,
  Bell, FilePlus, Plus, Filter, Search, RotateCcw
} from 'lucide-react';

const tabs = [
  { id: 'crm', label: 'CRM' },
  { id: 'leads', label: 'Leads' },
  { id: 'followups', label: 'Follow ups' },
  { id: 'campaigns', label: 'Campaigns' },
  {
    id: 'contactslogin', label: 'Contacts Login', hasDropdown: true,
    children: ['Contacts Login', 'Commissions']
  },
  { id: 'reports', label: 'Reports' },
  { id: 'proposal_template', label: 'Proposal template' },
  { id: 'proposals', label: 'Proposals' },
  { id: 'order_requests', label: 'Order Requests' },
  { id: 'b2b_marketplace', label: 'B2B Marketplace' },
  { id: 'sources', label: 'Sources' },
  { id: 'life_stage', label: 'Life Stage' },
  { id: 'followup_category', label: 'Followup Category' },
  { id: 'settings', label: 'Settings' },
];

const statCards = [
  { label: "Today's Follow ups", value: 0, icon: <Calendar size={22} color="#6366f1" />, bg: '#ede9fe' },
  { label: 'My Leads', value: 0, icon: <TrendingUp size={22} color="#3b82f6" />, bg: '#dbeafe' },
  { label: 'My leads to customer conversion', value: 0, icon: <UserCheck size={22} color="#10b981" />, bg: '#dcfce7' },
];

const followupStats = [
  { label: 'Scheduled', value: 0, color: '#6366f1' },
  { label: 'Open', value: 0, color: '#3b82f6' },
  { label: 'Cancelled', value: 0, color: '#ef4444' },
  { label: 'Completed', value: 0, color: '#22c55e' },
];

const globalStats = [
  { label: 'Customers', value: 47, icon: <Users size={20} color="#6366f1" /> },
  { label: 'Leads', value: 0, icon: <TrendingUp size={20} color="#3b82f6" /> },
  { label: 'Sources', value: 0, icon: <Tag size={20} color="#f59e0b" /> },
  { label: 'Life Stages', value: 0, icon: <Heart size={20} color="#ec4899" /> },
];

const CRM = ({ activeTab: propActiveTab }) => {
  const [activeTab, setActiveTab] = useState('crm');
  const [showContactsLoginDropdown, setShowContactsLoginDropdown] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Synchronize internal activeTab with propActiveTab
  React.useEffect(() => {
    if (propActiveTab) {
      if (propActiveTab === 'crm_dashboard' || propActiveTab === 'crm') {
        setActiveTab('crm');
      } else if (propActiveTab === 'crm_leads') {
        setActiveTab('leads');
      } else if (propActiveTab === 'crm_followups') {
        setActiveTab('followups');
      } else if (propActiveTab === 'crm_campaigns') {
        setActiveTab('campaigns');
      } else if (propActiveTab === 'crm_contactslogin') {
        setActiveTab('contacts_login');
      } else if (propActiveTab === 'crm_commissions') {
        setActiveTab('commissions');
      } else if (propActiveTab === 'crm_reports') {
        setActiveTab('reports');
      } else if (propActiveTab === 'crm_proposal_template') {
        setActiveTab('proposal_template');
      } else if (propActiveTab === 'crm_proposals') {
        setActiveTab('proposals');
      } else if (propActiveTab === 'crm_order_request' || propActiveTab === 'crm_order_requests') {
        setActiveTab('order_requests');
      } else if (propActiveTab === 'crm_marketplace' || propActiveTab === 'crm_b2b_marketplace') {
        setActiveTab('b2b_marketplace');
      } else if (propActiveTab === 'crm_sources') {
        setActiveTab('sources');
      } else if (propActiveTab === 'crm_life_stage') {
        setActiveTab('life_stage');
      } else if (propActiveTab === 'crm_followup_category') {
        setActiveTab('followup_category');
      } else if (propActiveTab === 'crm_settings') {
        setActiveTab('settings');
      } else if (propActiveTab === 'crm_call_log') {
        setActiveTab('call_logs');
      }
    }
  }, [propActiveTab]);

  // Data fetching state
  const [leadsData, setLeadsData] = useState([]);
  const [leadViewMode, setLeadViewMode] = useState('list');
  const [followUpsData, setFollowUpsData] = useState([]);
  const [followupSubTab, setFollowupSubTab] = useState('followups');
  const [followUpResources, setFollowUpResources] = useState({});
  const [campaignsData, setCampaignsData] = useState([]);
  const [crmLoading, setCrmLoading] = useState(false);
  const [callLogsData, setCallLogsData] = useState([]);
  const [contactsLoginData, setContactsLoginData] = useState([]);
  const [crmDashboardData, setCrmDashboardData] = useState(null);
  const [contactLoginResources, setContactLoginResources] = useState([]);
  const [reportsData, setReportsData] = useState({ byUser: [], byContact: [], conversion: [] });
  const [commissionsData, setCommissionsData] = useState([]);
  const [proposalTemplatesData, setProposalTemplatesData] = useState([]);
  const [proposalsData, setProposalsData] = useState([]);
  const [orderRequestsData, setOrderRequestsData] = useState([]);
  const [marketplaceData, setMarketplaceData] = useState(null);
  const [activeScheduleLogs, setActiveScheduleLogs] = useState(null);
  const [showScheduleLogsModal, setShowScheduleLogsModal] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [newLogDescription, setNewLogDescription] = useState('');

  // Fetch functions
  const fetchOrderRequests = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCrmOrderRequests();
      setOrderRequestsData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch order requests', e); */ }
    finally { setCrmLoading(false); }
  };

  const fetchMarketplace = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCrmMarketplace();
      setMarketplaceData(r?.data || r || null);
    } catch (e) { /* console.error('Failed to fetch marketplace', e); */ }
    finally { setCrmLoading(false); }
  };

  const fetchScheduleLogs = async (scheduleId) => {
    try {
      setSelectedScheduleId(scheduleId);
      const r = await apiService.getCrmScheduleLogs(scheduleId);
      setActiveScheduleLogs(r?.data || r?.log || r || []);
      setShowScheduleLogsModal(true);
    } catch (e) { /* console.error('Failed to fetch schedule logs', e); */ }
  };

  const formatDatetimeForBusinessUfDate = (val) => {
    if (!val) return '';
    let dateObj = null;
    if (typeof val === 'string') {
      if (val.includes('T')) {
        const parts = val.split('T');
        const [y, m, d] = parts[0].split('-').map(Number);
        const [hh, mm] = (parts[1] || '00:00').split(':').map(Number);
        dateObj = new Date(y, m - 1, d, hh, mm);
      } else {
        dateObj = new Date(val.replace(/-/g, '/'));
      }
    } else {
      dateObj = val;
    }
    if (!dateObj || isNaN(dateObj.getTime())) return val;

    const dateFormat = localStorage.getItem('etpos_date_format') || 'm/d/Y';
    const rawTimeFormat = localStorage.getItem('etpos_time_format');
    const is12Hr = rawTimeFormat ? (String(rawTimeFormat) === '12' || String(rawTimeFormat) === '12hr') : true;

    const yyyy = String(dateObj.getFullYear());
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');

    let datePart = `${mm}/${dd}/${yyyy}`;
    if (dateFormat === 'd-m-Y') datePart = `${dd}-${mm}-${yyyy}`;
    else if (dateFormat === 'm-d-Y') datePart = `${mm}-${dd}-${yyyy}`;
    else if (dateFormat === 'd/m/Y') datePart = `${dd}/${mm}/${yyyy}`;
    else if (dateFormat === 'Y-m-d') datePart = `${yyyy}-${mm}-${dd}`;
    else if (dateFormat === 'Y/m/d') datePart = `${yyyy}/${mm}/${dd}`;

    let hours = dateObj.getHours();
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');

    if (is12Hr) {
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const hh = String(hours).padStart(2, '0');
      return `${datePart} ${hh}:${minutes} ${ampm}`;
    } else {
      const hh = String(hours).padStart(2, '0');
      return `${datePart} ${hh}:${minutes}`;
    }
  };

  const renderCleanHtml = (htmlString) => {
    if (htmlString === null || htmlString === undefined || htmlString === '') return '-';
    if (typeof htmlString !== 'string') return String(htmlString);
    if (!htmlString.includes('<')) return htmlString;
    return <span dangerouslySetInnerHTML={{ __html: htmlString }} />;
  };

  const stripHtmlText = (html) => {
    if (!html) return '';
    if (typeof html !== 'string') return String(html);
    if (!html.includes('<')) return html.trim();
    return html.replace(/<[^>]*>?/gm, '').trim();
  };

  const handleAddScheduleLog = async () => {
    if (!newLogDescription.trim() || !selectedScheduleId) return;
    try {
      const nowFormatted = formatDatetimeForBusinessUfDate(new Date());
      await apiService.createCrmScheduleLog({
        schedule_id: selectedScheduleId,
        description: newLogDescription,
        log_type: 'call',
        start_datetime: nowFormatted,
        end_datetime: nowFormatted
      });
      setNewLogDescription('');
      fetchScheduleLogs(selectedScheduleId);
    } catch (e) { /* console.error('Failed to add schedule log', e); */ }
  };
  const fetchLeads = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCrmLeads({ per_page: -1 });
      setLeadsData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch leads', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchFollowUps = async (subTabVal) => {
    setCrmLoading(true);
    try {
      const activeSub = subTabVal !== undefined ? subTabVal : followupSubTab;
      const isRecurring = activeSub === 'recurring';
      const r = await apiService.getCrmFollowUps({ per_page: -1, is_recursive: isRecurring ? 1 : 0 });
      setFollowUpsData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch follow-ups', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchFollowUpResources = async () => {
    try {
      if (typeof fetchLeads === 'function') fetchLeads();
      if (typeof fetchFollowupCategories === 'function') fetchFollowupCategories();
      if (typeof fetchCrmCreateResources === 'function') fetchCrmCreateResources();
      const r = await apiService.getCrmFollowUpResources();
      setFollowUpResources(r?.data || r || {});
    } catch (e) { /* console.error('Failed to fetch follow-up resources', e); */ }
  };
  const fetchCampaigns = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCrmCampaigns({ per_page: -1 });
      setCampaignsData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch campaigns', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchSources = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCategories('crm_source');
      setSourcesData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch sources', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchLifeStages = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCategories('life_stage');
      setLifeStagesData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch life stages', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchFollowupCategories = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCategories('followup_category');
      setFollowupCategoriesData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch followup categories', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchCrmSettings = async () => {
    try {
      const r = await apiService.getCrmSettings();
      const settings = r?.data?.crm_settings || r?.crm_settings || {};
      setCrmSettingsData({
        enable_order_request: settings.enable_order_request || false,
        order_request_prefix: settings.order_request_prefix || ''
      });
    } catch (e) { /* console.error('Failed to fetch CRM settings', e); */ }
  };
  const fetchContactsLogin = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCrmContactsLogin();
      setContactsLoginData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch contacts login', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchCallLogs = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCrmCallLogs();
      setCallLogsData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch call logs', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchCrmDashboard = async () => {
    try {
      const r = await apiService.getCrmDashboard();
      setCrmDashboardData(r?.data || r || null);
    } catch (e) { /* console.error('Failed to fetch CRM dashboard', e); */ }
  };
  const fetchContactLoginResources = async () => {
    try {
      const r = await apiService.getCrmContactLoginResources();
      setContactLoginResources(r?.contacts || r?.data?.contacts || r?.data || []);
    } catch (e) { /* console.error('Failed to fetch contact login resources', e); */ }
  };
  const fetchReports = async () => {
    setCrmLoading(true);
    try {
      const reportParams = { start_date: '2026-01-01', end_date: '2026-12-31' };
      const [byUser, byContact, conversion] = await Promise.all([
        apiService.getCrmReports('follow-ups-by-user', reportParams),
        apiService.getCrmReports('follow-ups-by-contact', reportParams),
        apiService.getCrmReports('lead-to-customer', reportParams)
      ]);
      setReportsData({
        byUser: byUser?.data || [],
        byContact: byContact?.data || [],
        conversion: conversion?.data || []
      });
    } catch (e) { /* console.error('Failed to fetch reports', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchCommissions = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCrmCommissions();
      setCommissionsData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch commissions', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchProposalTemplates = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCrmProposalTemplates();
      const tmpl = r?.proposal_template || r?.data || r;
      setProposalTemplatesData(tmpl ? (Array.isArray(tmpl) ? tmpl : [tmpl]) : []);
    } catch (e) { /* console.error('Failed to fetch proposal templates', e); */ }
    finally { setCrmLoading(false); }
  };
  const fetchProposals = async () => {
    setCrmLoading(true);
    try {
      const r = await apiService.getCrmProposals();
      setProposalsData(r?.data || []);
    } catch (e) { /* console.error('Failed to fetch proposals', e); */ }
    finally { setCrmLoading(false); }
  };

  const getLeadNumericId = (leadOrId) => {
    if (!leadOrId) return null;
    if (typeof leadOrId === 'number') return leadOrId;
    if (typeof leadOrId === 'string' && /^\d+$/.test(leadOrId.trim())) return leadOrId.trim();

    if (typeof leadOrId === 'object' && leadOrId !== null) {
      if (leadOrId.id && /^\d+$/.test(String(leadOrId.id))) return leadOrId.id;
      if (leadOrId.DT_RowId && /^\d+$/.test(String(leadOrId.DT_RowId))) return leadOrId.DT_RowId;

      if (leadOrId.action && typeof leadOrId.action === 'string') {
        const match = leadOrId.action.match(/\/(?:lead|leads)\/(\d+)/i);
        if (match && match[1]) return match[1];
      }

      if (leadOrId.upcoming_follow_up && typeof leadOrId.upcoming_follow_up === 'string') {
        const match = leadOrId.upcoming_follow_up.match(/contact_id=(\d+)/i);
        if (match && match[1]) return match[1];
      }

      if (leadOrId.last_follow_up && typeof leadOrId.last_follow_up === 'string') {
        const match = leadOrId.last_follow_up.match(/contact_id=(\d+)/i);
        if (match && match[1]) return match[1];
      }

      if (leadOrId.contact_id && /^\d+$/.test(String(leadOrId.contact_id))) return leadOrId.contact_id;
      return leadOrId.id || leadOrId.contact_id;
    }

    if (typeof leadOrId === 'string') {
      const match = leadOrId.match(/\/(?:lead|leads)\/(\d+)/i);
      if (match && match[1]) return match[1];
    }

    return leadOrId;
  };

  const getFollowUpId = (item, fallbackIndex) => {
    if (!item) return fallbackIndex;
    if (typeof item === 'number') return item;
    if (typeof item === 'string' && /^\d+$/.test(item.trim())) return item.trim();
    if (typeof item === 'object') {
      if (item.id && /^\d+$/.test(String(item.id))) return item.id;
      if (item.schedule_id && /^\d+$/.test(String(item.schedule_id))) return item.schedule_id;
      if (item.DT_RowId) return String(item.DT_RowId).replace(/\D/g, '');
      if (typeof item.action === 'string') {
        const m1 = item.action.match(/data-schedule_id=["']?(\d+)["']?/i);
        if (m1) return m1[1];
        const m2 = item.action.match(/\/crm\/follow-ups\/(\d+)/i);
        if (m2) return m2[1];
        const m3 = item.action.match(/\/crm\/follow-up-log\?schedule_id=(\d+)/i);
        if (m3) return m3[1];
      }
    }
    return fallbackIndex;
  };

  const handleDeleteLead = async (leadOrId) => {
    const id = getLeadNumericId(leadOrId);

    if (!id || String(id) === 'undefined') {
      toast.error('Invalid lead ID.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await apiService.deleteCrmLead(id);
      toast.success('Lead deleted successfully!');
      fetchLeads();
    } catch (e) {
      /* console.error(e); */
      toast.error('Failed to delete lead.');
    }
  };

  const handleConvertLead = async (leadOrId) => {
    const id = getLeadNumericId(leadOrId);

    if (!id || String(id) === 'undefined') {
      toast.error('Invalid lead ID.');
      return;
    }

    if (!window.confirm('Convert this lead to customer?')) return;
    setCrmLoading(true);
    try {
      let res;
      if (typeof apiService.convertLeadToCustomer === 'function') {
        res = await apiService.convertLeadToCustomer(id);
      } else if (typeof apiService.convertCrmLead === 'function') {
        res = await apiService.convertCrmLead(id);
      }
      if (res && (res.success === true || res.success === 1)) {
        toast.success(res.msg || 'Lead successfully converted to customer!');
      } else if (res && res.success === false) {
        toast.error(res.msg || 'Failed to convert lead to customer.');
      } else {
        toast.success('Lead successfully converted to customer!');
      }
      await fetchLeads();
    } catch (e) {
      /* console.error('Error converting lead:', e); */
      toast.error(e?.message || 'Failed to convert lead.');
    } finally {
      setCrmLoading(false);
    }
  };

  const handleViewFollowUpLog = async (item) => {
    const id = getFollowUpId(item, item.id);
    setOpenFollowUpActionId(null);
    setActiveScheduleId(id);
    setCrmLoading(true);
    try {
      const res = await apiService.getCrmFollowUpLog(id);
      setFollowUpLogData(res?.logs || res?.data || res || []);
      setShowFollowUpLogModal(true);
    } catch (e) {
      /* console.error(e); */
      toast.error('Failed to load follow up log.');
    } finally {
      setCrmLoading(false);
    }
  };

  const handleAddFollowUpLog = (item) => {
    const id = getFollowUpId(item, item.id);
    setOpenFollowUpActionId(null);
    setActiveScheduleId(id);
    setNewLogDescription('');
    setShowAddFollowUpLogModal(true);
  };

  const handleEditFollowUp = async (item) => {
    const id = getFollowUpId(item, item.id);
    setOpenFollowUpActionId(null);
    try {
      const res = await apiService.getCrmFollowUpEdit(id);
      const sched = res?.schedule || res?.data?.schedule || res?.data || item;
      setEditingFollowupId(id);
      setFollowupForm({
        title: sched.title || '',
        customer: sched.contact_id || '',
        status: sched.status || '',
        startDatetime: sched.start_datetime ? sched.start_datetime.replace(' ', 'T').slice(0, 16) : '',
        endDatetime: sched.end_datetime ? sched.end_datetime.replace(' ', 'T').slice(0, 16) : '',
        description: sched.description || '',
        followupType: sched.schedule_type || '',
        followupCategory: sched.followup_category_id || '',
        assignedTo: Array.isArray(sched.users) ? sched.users.map(u => String(u.id || u.user_id)) : [],
        sendNotification: Boolean(sched.send_notification)
      });
      fetchFollowUpResources();
      setShowAddFollowupModal(true);
    } catch (e) {
      /* console.error(e); */
      toast.error('Failed to load follow up data for editing.');
    }
  };

  const handleDeleteFollowup = async (item) => {
    const id = getFollowUpId(item, item.id);
    setOpenFollowUpActionId(null);
    if (!window.confirm('Are you sure you want to delete this follow-up?')) return;
    try {
      await apiService.deleteCrmFollowUp(id);
      toast.success('Follow-up deleted successfully!');
      fetchFollowUps();
    } catch (e) {
      /* console.error(e); */
      toast.error('Failed to delete follow-up.');
    }
  };

  const handleDeleteCampaign = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await apiService.deleteCrmCampaign(id);
      toast.success('Campaign deleted successfully!');
      fetchCampaigns();
    } catch (e) {
      /* console.error(e); */
      toast.error('Failed to delete campaign.');
    }
  };

  const handleSendCampaignNotification = async (id) => {
    try {
      await apiService.sendCampaignNotification(id);
      toast.success('Campaign notification sent successfully!');
      fetchCampaigns();
    } catch (e) {
      /* console.error(e); */
      toast.error('Failed to send campaign notification.');
    }
  };

  useEffect(() => {
    fetchFollowUpResources();
    fetchCrmDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'leads') fetchLeads();
    if (activeTab === 'followups') fetchFollowUps();
    if (activeTab === 'campaigns') fetchCampaigns();
    if (activeTab === 'sources') fetchSources();
    if (activeTab === 'life_stage') fetchLifeStages();
    if (activeTab === 'followup_category') fetchFollowupCategories();
    if (activeTab === 'settings') fetchCrmSettings();
    if (activeTab === 'contacts_login') fetchContactsLogin();
    if (activeTab === 'call_logs') fetchCallLogs();
    if (activeTab === 'reports') fetchReports();
    if (activeTab === 'commissions') fetchCommissions();
    if (activeTab === 'proposal_template') fetchProposalTemplates();
    if (activeTab === 'proposals') fetchProposals();
    if (activeTab === 'order_requests') fetchOrderRequests();
    if (activeTab === 'b2b_marketplace') fetchMarketplace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, followupSubTab]);

  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showExport2Dropdown, setShowExport2Dropdown] = useState(false);
  const [showLeadsExportDropdown, setShowLeadsExportDropdown] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showAddFollowupModal, setShowAddFollowupModal] = useState(false);
  const [showFollowupFilters, setShowFollowupFilters] = useState(false);
  const [showLeadsFilters, setShowLeadsFilters] = useState(false);
  const [showFollowupExportDropdown, setShowFollowupExportDropdown] = useState(false);
  const [followupEntries, setFollowupEntries] = useState('25');
  const [followupSearch, setFollowupSearch] = useState('');
  const [followupFilters, setFollowupFilters] = useState({ contact: '', assigned_to: '', status: '', schedule_type: '', followup_category: '', date_range: '' });

  const [dateRange] = useState('01/01/2026 - 12/31/2026');
  const [entries1, setEntries1] = useState('25');
  const [entries2, setEntries2] = useState('25');
  const [leadsEntries, setLeadsEntries] = useState('25');
  const [search1, setSearch1] = useState('');
  const [search2, setSearch2] = useState('');
  const [leadsSearch, setLeadsSearch] = useState('');
  const [leadsSourceFilter, setLeadsSourceFilter] = useState('');
  const [leadsLifeStageFilter, setLeadsLifeStageFilter] = useState('');
  const [leadsAssignedToFilter, setLeadsAssignedToFilter] = useState('');
  const [leadsFollowupStatusFilter, setLeadsFollowupStatusFilter] = useState('');
  const [leadsFollowupTypeFilter, setLeadsFollowupTypeFilter] = useState('');
  const [openLeadActionId, setOpenLeadActionId] = useState(null);
  const [showLeadDetailsModal, setShowLeadDetailsModal] = useState(false);
  const [leadDetailsData, setLeadDetailsData] = useState(null);
  const [editingLeadId, setEditingLeadId] = useState(null);

  const [openFollowUpActionId, setOpenFollowUpActionId] = useState(null);
  const [showFollowUpLogModal, setShowFollowUpLogModal] = useState(false);
  const [followUpLogData, setFollowUpLogData] = useState([]);
  const [editingFollowupId, setEditingFollowupId] = useState(null);
  const [showAddFollowUpLogModal, setShowAddFollowUpLogModal] = useState(false);
  const [activeScheduleId, setActiveScheduleId] = useState(null);

  const [campaignsSearch, setCampaignsSearch] = useState('');
  const [campaignsEntries, setCampaignsEntries] = useState('25');
  const [showAddCampaignModal, setShowAddCampaignModal] = useState(false);
  const [showCampaignExportDropdown, setShowCampaignExportDropdown] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    type: 'Email',
    to: '',
    subject: '',
    body: ''
  });

  const [showAddContactLoginModal, setShowAddContactLoginModal] = useState(false);
  const [editingContactLoginId, setEditingContactLoginId] = useState(null);
  const [openContactLoginActionId, setOpenContactLoginActionId] = useState(null);
  const [contactLoginSearch, setContactLoginSearch] = useState('');
  const [contactLoginEntries, setContactLoginEntries] = useState('25');
  const [showContactLoginExportDropdown, setShowContactLoginExportDropdown] = useState(false);
  const [showContactLoginFilters, setShowContactLoginFilters] = useState(false);
  const [contactLoginForm, setContactLoginForm] = useState({
    contact: '',
    email: '',
    mobile: '',
    alternate: '',
    familyContact: '',
    department: '',
    designation: '',
    commission: '',
    isActive: true,
    allowLogin: true
  });

  useEffect(() => {
    const handleGlobalClick = () => {
      setOpenContactLoginActionId(null);
      setOpenLeadActionId(null);
      setOpenFollowUpActionId(null);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  const [commissionsSearch, setCommissionsSearch] = useState('');
  const [commissionsEntries, setCommissionsEntries] = useState('25');
  const [showCommissionsExportDropdown, setShowCommissionsExportDropdown] = useState(false);
  const [showCommissionsFilters, setShowCommissionsFilters] = useState(false);

  const [reportsSearch, setReportsSearch] = useState({ user: '', contact: '', conversion: '' });
  const [reportsEntries, setReportsEntries] = useState({ user: '25', contact: '25', conversion: '25' });
  const [showReportsExport, setShowReportsExport] = useState({ user: false, contact: false, conversion: false });
  const [showReportFollowupsModal, setShowReportFollowupsModal] = useState(false);
  const [reportFollowupsFilter, setReportFollowupsFilter] = useState({ title: '', assigned_to: '', status: '', contact_id: '' });
  const [reportFollowupsList, setReportFollowupsList] = useState([]);
  const [reportFollowupsLoading, setReportFollowupsLoading] = useState(false);

  const renderFollowupsTab = () => {
    const isRecurring = followupSubTab === 'recurring';
    const displayList = followUpsData.filter(f => {
      const isRec = isRecurring ? (f.is_recursive == 1 || f.is_recursive === true) : (f.is_recursive != 1);
      if (!isRec) return false;

      const matchesSearch = !followupSearch || [
        f.contact, f.contact_name, f.customer?.name, f.title, f.description, f.status, f.schedule_type, f.followup_category
      ].some(val => String(val || '').toLowerCase().includes(followupSearch.toLowerCase()));

      const matchesContact = !followupFilters.contact || String(f.contact_id || f.contact || '') === String(followupFilters.contact);
      const matchesAssigned = !followupFilters.assigned_to || (
        Array.isArray(f.users) ? f.users.some(u => String(u.id || u.user_id || u) === String(followupFilters.assigned_to)) : String(f.assigned_to || f.user_id || '') === String(followupFilters.assigned_to)
      );
      const matchesStatus = !followupFilters.status || String(f.status || '').toLowerCase() === String(followupFilters.status).toLowerCase();
      const matchesType = !followupFilters.schedule_type || String(f.schedule_type || f.followup_type || '').toLowerCase() === String(followupFilters.schedule_type).toLowerCase();

      return matchesSearch && matchesContact && matchesAssigned && matchesStatus && matchesType;
    });

    const statusCounts = {};
    const typeCounts = {};
    displayList.forEach(f => {
      const cleanStatus = stripHtmlText(f.status) || 'Scheduled';
      if (cleanStatus) statusCounts[cleanStatus] = (statusCounts[cleanStatus] || 0) + 1;
      const cleanType = stripHtmlText(f.schedule_type || f.followup_type);
      if (cleanType) typeCounts[cleanType] = (typeCounts[cleanType] || 0) + 1;
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {/* Main Follow ups Card */}
        <div style={{ background: 'var(--etpos-card, #ffffff)', borderRadius: '10px', border: '1px solid var(--etpos-border, #e2e8f0)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <div className="crm-card-body" style={{ padding: '1.5rem' }}>

            {/* Header Title & Top Buttons */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: 'var(--etpos-text, #0f172a)' }}>All Follow ups</h3>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => { fetchFollowUpResources(); setEditingFollowupId(null); setFollowupForm({ title: '', customer: '', status: '', startDatetime: '', endDatetime: '', description: '', followupType: '', followupCategory: '', assignedTo: [], sendNotification: false }); setShowAddFollowupModal(true); }} style={{ background: '#7c3aed', color: '#ffffff', border: 'none', borderRadius: '20px', padding: '0.45rem 1rem', fontWeight: '700', fontSize: '0.825rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(124, 58, 237, 0.25)' }}>
                  + Add advance follow up
                </button>
                <button onClick={() => { fetchFollowUpResources(); setEditingFollowupId(null); setFollowupForm({ title: '', customer: '', status: '', startDatetime: '', endDatetime: '', description: '', followupType: '', followupCategory: '', assignedTo: [], sendNotification: false }); setShowAddFollowupModal(true); }} style={{ background: '#3b82f6', color: '#ffffff', border: 'none', borderRadius: '20px', padding: '0.45rem 1rem', fontWeight: '700', fontSize: '0.825rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(59, 130, 246, 0.25)' }}>
                  + Add
                </button>
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="crm-sub-tabs" style={{ borderBottom: '2px solid var(--etpos-border, #e2e8f0)', marginBottom: '1.25rem', display: 'flex', gap: '1rem', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <button onClick={() => { setFollowupSubTab('followups'); fetchFollowUps('followups'); }} style={{ background: 'none', border: 'none', padding: '0.75rem 0.5rem', fontSize: '0.925rem', fontWeight: followupSubTab === 'followups' ? '700' : '500', color: followupSubTab === 'followups' ? '#2563eb' : 'var(--etpos-text-muted, #64748b)', borderBottom: followupSubTab === 'followups' ? '3px solid #2563eb' : '3px solid transparent', cursor: 'pointer', marginBottom: '-2px', whiteSpace: 'nowrap' }}>
                Follow ups
              </button>
              <button onClick={() => { setFollowupSubTab('recurring'); fetchFollowUps('recurring'); }} style={{ background: 'none', border: 'none', padding: '0.75rem 0.5rem', fontSize: '0.925rem', fontWeight: followupSubTab === 'recurring' ? '700' : '500', color: followupSubTab === 'recurring' ? '#2563eb' : 'var(--etpos-text-muted, #64748b)', borderBottom: followupSubTab === 'recurring' ? '3px solid #2563eb' : '3px solid transparent', cursor: 'pointer', marginBottom: '-2px', whiteSpace: 'nowrap' }}>
                Recurring Follow up
              </button>
            </div>

            {/* Table Controls Toolbar (Show entries, Export buttons, Search) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--etpos-text, #475569)' }}>
                <span>Show</span>
                <select value={followupEntries} onChange={e => setFollowupEntries(e.target.value)} style={{ padding: '0.35rem 0.6rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '4px', fontSize: '0.85rem' }}>
                  <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>

              {/* Center Export Pill Buttons matching Web */}
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                {['Export CSV', 'Export Excel', 'Print', 'Column visibility', 'Export PDF'].map(btn => (
                  <button key={btn} style={{ padding: '0.35rem 0.75rem', background: 'var(--etpos-card, #ffffff)', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '4px', fontSize: '0.78rem', fontWeight: '600', color: 'var(--etpos-text, #334155)', cursor: 'pointer' }}>
                    {btn}
                  </button>
                ))}
              </div>

              {/* Right Search Input with Filters Button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input type="text" value={followupSearch} onChange={e => setFollowupSearch(e.target.value)} placeholder="Search..." style={{ padding: '0.4rem 0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '4px', fontSize: '0.875rem', minWidth: '180px' }} />
                <button onClick={() => setShowFollowupFilters(true)} style={{ padding: '0.4rem 0.75rem', background: 'var(--etpos-card, #ffffff)', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '4px', fontSize: '0.85rem', fontWeight: '600', color: '#2563eb', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg> Filters
                </button>
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '2px solid var(--etpos-border, #e2e8f0)' }}>
                    {!isRecurring ? (
                      ['Action', 'Contact', 'Start Datetime', 'End Datetime', 'Status', 'Follow Up Type', 'Followup Category', 'Assigned to', 'Description', 'Additional info', 'Title', 'Added By', 'Added On'].map((h, i) => (
                        <th key={i} style={{ padding: '0.75rem 0.85rem', textAlign: 'left', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))
                    ) : (
                      ['Action', 'Status', 'Follow Up Type', 'Followup Category', 'Follow up by', 'In days', 'Assigned to', 'Description', 'Additional info', 'Title', 'Added By', 'Added On'].map((h, i) => (
                        <th key={i} style={{ padding: '0.75rem 0.85rem', textAlign: 'left', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))
                    )}
                  </tr>
                </thead>
                <tbody>
                  {crmLoading ? (
                    <tr><td colSpan={isRecurring ? 12 : 13} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                  ) : displayList.length === 0 ? (
                    <tr><td colSpan={isRecurring ? 12 : 13} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)' }}>No data available in table</td></tr>
                  ) : (
                    displayList.map((f, i) => {
                      const itemKey = getFollowUpId(f, i);
                      return (
                        <tr key={itemKey} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          {/* Action Cell */}
                          <td style={{ padding: '0.65rem 0.85rem', position: 'relative' }}>
                            <div style={{ position: 'relative' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenFollowUpActionId(openFollowUpActionId === itemKey ? null : itemKey);
                                }}
                                style={{ background: '#e0f2fe', color: '#0284c7', border: '1px solid #bae6fd', borderRadius: '15px', padding: '0.3rem 0.85rem', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                              >
                                Action <ChevronDown size={12} />
                              </button>
                              {openFollowUpActionId === itemKey && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, background: 'var(--etpos-card, #ffffff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 4px 14px rgba(0,0,0,0.15)', zIndex: 100, minWidth: '175px', padding: '0.35rem 0' }}>
                                  {!isRecurring ? (
                                    <>
                                      <button onClick={() => handleViewFollowUpLog(f)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.45rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.825rem', color: 'var(--etpos-text, #0f172a)', cursor: 'pointer' }}>
                                        👁 View follow up
                                      </button>
                                      <button onClick={() => handleAddFollowUpLog(f)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.45rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.825rem', color: 'var(--etpos-text, #0f172a)', cursor: 'pointer' }}>
                                        📝 Add Follow Up Log
                                      </button>
                                      <button onClick={() => handleEditFollowUp(f)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.45rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.825rem', color: 'var(--etpos-text, #0f172a)', cursor: 'pointer' }}>
                                        ✏️ Edit
                                      </button>
                                      <button onClick={() => handleDeleteFollowup(f)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.45rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.825rem', color: '#ef4444', cursor: 'pointer' }}>
                                        🗑 Delete
                                      </button>
                                    </>
                                  ) : (
                                    <button onClick={() => handleDeleteFollowup(f)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.45rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.825rem', color: '#ef4444', cursor: 'pointer' }}>
                                      🗑 Delete
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>

                          {!isRecurring && (
                            <td style={{ padding: '0.65rem 0.85rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#2563eb', fontWeight: '600' }}>
                                <span>{stripHtmlText(f.contact || f.contact_name || f.customer?.name) || '-'}</span>
                                <span style={{ fontSize: '0.75rem', cursor: 'pointer' }}>📝</span>
                              </div>
                            </td>
                          )}

                          {!isRecurring && (
                            <td style={{ padding: '0.65rem 0.85rem', whiteSpace: 'nowrap' }}>
                              <div>{stripHtmlText(f.start_datetime) || '-'}</div>
                            </td>
                          )}

                          {!isRecurring && (
                            <td style={{ padding: '0.65rem 0.85rem', whiteSpace: 'nowrap' }}>
                              <div>{stripHtmlText(f.end_datetime) || '-'}</div>
                            </td>
                          )}

                          <td style={{ padding: '0.65rem 0.85rem' }}>
                            <span style={{ padding: '0.2rem 0.6rem', background: '#ffedd5', color: '#c2410c', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'capitalize' }}>
                              {stripHtmlText(f.status) || 'Scheduled'}
                            </span>
                          </td>

                          <td style={{ padding: '0.65rem 0.85rem' }}>{stripHtmlText(f.schedule_type || f.followup_type) || '-'}</td>
                          <td style={{ padding: '0.65rem 0.85rem' }}>{stripHtmlText(f.followup_category?.name || f.followup_category || f.category) || '-'}</td>

                          {isRecurring && (
                            <td style={{ padding: '0.65rem 0.85rem' }}>{stripHtmlText(f.follow_up_by) || 'Payment Status - All'}</td>
                          )}
                          {isRecurring && (
                            <td style={{ padding: '0.65rem 0.85rem' }}>{f.in_days || '2'}</td>
                          )}

                          <td style={{ padding: '0.65rem 0.85rem' }}>
                            <div style={{ display: 'flex', gap: '0.2rem' }}>
                              {Array.isArray(f.users) && f.users.length > 0 ? (
                                f.users.map((u, idx) => (
                                  <span key={idx} title={u.first_name || u.name} style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#94a3b8', color: '#ffffff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '700' }}>
                                    {(u.first_name || u.name || 'U').charAt(0).toUpperCase()}
                                  </span>
                                ))
                              ) : (
                                <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#cbd5e1', color: 'var(--etpos-text, #475569)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: '700' }}>
                                  AR
                                </span>
                              )}
                            </div>
                          </td>

                          <td style={{ padding: '0.65rem 0.85rem' }}>{stripHtmlText(f.description) || '-'}</td>
                          <td style={{ padding: '0.65rem 0.85rem' }}>{stripHtmlText(f.additional_info) || '-'}</td>
                          <td style={{ padding: '0.65rem 0.85rem' }}>{stripHtmlText(f.title) || '-'}</td>
                          <td style={{ padding: '0.65rem 0.85rem' }}>{stripHtmlText(f.added_by || [f.surname, f.first_name, f.last_name].filter(Boolean).join(' ')) || '-'}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>

                {/* Table Footer Total Row matching Web */}
                <tfoot>
                  <tr style={{ background: '#e2e8f0', fontWeight: '700', borderTop: '2px solid #cbd5e1' }}>
                    <td colSpan={!isRecurring ? 13 : 12} style={{ padding: '0.6rem 1rem' }}>
                      <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.85rem', color: 'var(--etpos-text, #1e293b)' }}>
                        <span>Total:</span>
                        {Object.entries(statusCounts).map(([s, cnt]) => (
                          <span key={s}>{s} - {cnt}</span>
                        ))}
                        {Object.entries(typeCounts).map(([t, cnt]) => (
                          <span key={t}>{t} - {cnt}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Pagination Controls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--etpos-text-muted, #64748b)' }}>
                Showing {displayList.length > 0 ? 1 : 0} to {displayList.length} of {displayList.length} entries
              </span>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button style={{ padding: '0.4rem 0.85rem', border: '1px solid var(--etpos-border, #cbd5e1)', background: 'var(--etpos-bg, #f8fafc)', color: 'var(--etpos-text-muted, #64748b)', borderRadius: '4px', cursor: 'not-allowed', fontSize: '0.825rem' }}>Previous</button>
                <button style={{ padding: '0.4rem 0.85rem', border: 'none', background: '#3b82f6', color: '#ffffff', borderRadius: '4px', cursor: 'pointer', fontSize: '0.825rem', fontWeight: '700' }}>1</button>
                <button style={{ padding: '0.4rem 0.85rem', border: '1px solid var(--etpos-border, #cbd5e1)', background: 'var(--etpos-bg, #f8fafc)', color: 'var(--etpos-text-muted, #64748b)', borderRadius: '4px', cursor: 'not-allowed', fontSize: '0.825rem' }}>Next</button>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  };

  const handleOpenReportFollowupsModal = async ({ title, assigned_to, status, contact_id, contact_name }) => {
    setReportFollowupsFilter({ title, assigned_to, status, contact_id });
    setShowReportFollowupsModal(true);
    setReportFollowupsLoading(true);
    try {
      const params = {
        start_date_time: '2026-01-01 00:00:00',
        end_date_time: '2026-12-31 23:59:59'
      };
      if (assigned_to) {
        params.assgined_to = assigned_to;
      }
      if (status && status !== 'all') params.status = status;
      if (contact_id) params.contact_id = contact_id;
      const res = await apiService.getCrmFollowUps(params);
      let list = res?.data || [];
      if (assigned_to) {
        list = list.filter(f => {
          if (f.users && Array.isArray(f.users)) {
            return f.users.some(u => String(u.id || u.user_id) === String(assigned_to));
          }
          return true;
        });
      }
      if (contact_id) {
        list = list.filter(f => String(f.contact_id) === String(contact_id));
      } else if (contact_name) {
        const targetName = String(contact_name).replace(/<[^>]*>?/gm, '').toLowerCase().trim();
        list = list.filter(f => {
          const cName = String(f.contact || f.biz_name || '').replace(/<[^>]*>?/gm, '').toLowerCase().trim();
          return cName.includes(targetName) || targetName.includes(cName);
        });
      }
      if (status && status !== 'all') {
        list = list.filter(f => {
          if (status === 'none') return !f.status;
          return String(f.status).toLowerCase() === String(status).toLowerCase();
        });
      }
      setReportFollowupsList(list);
    } catch (e) {
      /* console.error('Failed to fetch followups for report modal:', e); */
      setReportFollowupsList([]);
    } finally {
      setReportFollowupsLoading(false);
    }
  };

  const [proposalTemplatesSearch, setProposalTemplatesSearch] = useState('');
  const [proposalTemplatesEntries, setProposalTemplatesEntries] = useState('25');
  const [showAddProposalTemplateModal, setShowAddProposalTemplateModal] = useState(false);
  const [proposalTemplateForm, setProposalTemplateForm] = useState({ title: '', subject: '', body: '' });

  const [proposalsSearch, setProposalsSearch] = useState('');
  const [proposalsEntries, setProposalsEntries] = useState('25');
  const [showSendProposalModal, setShowSendProposalModal] = useState(false);
  const [proposalForm, setProposalForm] = useState({ contact_id: '', cc: '', bcc: '', subject: '', body: '' });

  const [sourcesSearch, setSourcesSearch] = useState('');
  const [sourcesEntries, setSourcesEntries] = useState('25');
  const [showAddSourceModal, setShowAddSourceModal] = useState(false);
  const [sourceForm, setSourceForm] = useState({ name: '', description: '' });

  const [lifeStageSearch, setLifeStageSearch] = useState('');
  const [lifeStageEntries, setLifeStageEntries] = useState('25');
  const [showAddLifeStageModal, setShowAddLifeStageModal] = useState(false);
  const [lifeStageForm, setLifeStageForm] = useState({ name: '', description: '' });

  const [followupCategorySearch, setFollowupCategorySearch] = useState('');
  const [followupCategoryEntries, setFollowupCategoryEntries] = useState('25');
  const [showAddFollowupCategoryModal, setShowAddFollowupCategoryModal] = useState(false);
  const [followupCategoryForm, setFollowupCategoryForm] = useState({ name: '', description: '' });

  const [sourcesData, setSourcesData] = useState([]);
  const [lifeStagesData, setLifeStagesData] = useState([]);
  const [followupCategoriesData, setFollowupCategoriesData] = useState([]);
  const [crmSettingsData, setCrmSettingsData] = useState({
    enable_order_request: false,
    order_request_prefix: ''
  });

  const [crmSettings, setCrmSettings] = useState({
    autoAssign: true,
    defaultLeadSource: 'Website',
    followupReminder: '2',
    proposalValidity: '30',
    enableNotifications: true
  });

  const [leadForm, setLeadForm] = useState({
    contactType: 'Lead', contactKind: 'Individual', contactId: '',
    customerGroup: '', firstName: '', lastName: '', mobile: '', alternate: '', landline: '', email: '',
    source: '', lifeStage: '', assignedTo: []
  });

  const [crmResources, setCrmResources] = useState({
    sources: [],
    lifeStages: [],
    users: [
      { id: 1, name: 'Mr Ajay Gogia' },
      { id: 2, name: 'Mr Pands' },
      { id: 3, name: 'shahabaj bagwan' }
    ]
  });



  const normalizeOptions = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data.map(item => (item && typeof item === 'object' ? item : { id: item, name: item }));
    return Object.entries(data).map(([id, name]) => ({ id, name }));
  };

  const fetchCrmCreateResources = async () => {
    try {
      const r = await apiService.getCrmLeadCreateResources();
      const data = r?.data || r || {};
      const fetchedUsers = normalizeOptions(data.users);
      setCrmResources(prev => ({
        sources: normalizeOptions(data.sources).length ? normalizeOptions(data.sources) : prev.sources,
        lifeStages: normalizeOptions(data.life_stages).length ? normalizeOptions(data.life_stages) : prev.lifeStages,
        users: fetchedUsers.length ? fetchedUsers : prev.users
      }));
    } catch (e) {
      /* console.error('Failed to fetch CRM resources:', e); */
    }
  };

  const [followupForm, setFollowupForm] = useState({
    title: '',
    customer: '',
    status: '',
    startDatetime: '',
    endDatetime: '',
    description: '',
    followupType: '',
    followupCategory: '',
    assignedTo: [],
    sendNotification: false
  });

  const handleViewLeadDetails = async (leadOrId) => {
    setOpenLeadActionId(null);
    const leadObj = typeof leadOrId === 'object' && leadOrId !== null ? leadOrId : null;
    const targetId = getLeadNumericId(leadOrId);
    const fallbackLead = leadObj || (leadsData.find(l => String(l.id || l.contact_id) === String(targetId)) || null);

    if (!targetId || String(targetId) === 'undefined') {
      if (fallbackLead) {
        setLeadDetailsData(fallbackLead);
        setShowLeadDetailsModal(true);
        return;
      }
      toast.error('Invalid lead ID.');
      return;
    }

    setCrmLoading(true);
    try {
      const res = await apiService.getCrmLeadDetails(targetId);
      const contactObj = res?.contact || res?.data?.contact || res?.data || res;
      setLeadDetailsData(contactObj || fallbackLead);
      setShowLeadDetailsModal(true);
    } catch (e) {
      /* console.warn('Could not fetch lead details from API, rendering local lead data:', e); */
      if (fallbackLead) {
        setLeadDetailsData(fallbackLead);
        setShowLeadDetailsModal(true);
      } else {
        toast.error('Failed to load lead details.');
      }
    } finally {
      setCrmLoading(false);
    }
  };

  const handleEditLead = (lead) => {
    setOpenLeadActionId(null);
    setEditingLeadId(lead.id);
    setLeadForm({
      contactType: lead.type ? (lead.type.charAt(0).toUpperCase() + lead.type.slice(1)) : 'Lead',
      contactKind: lead.supplier_business_name ? 'Business' : 'Individual',
      contactId: lead.contact_id || '',
      customerGroup: lead.customer_group_id || '',
      firstName: lead.first_name || lead.name || '',
      lastName: lead.last_name || '',
      mobile: lead.mobile || '',
      alternate: lead.alternate_number || '',
      landline: lead.landline || '',
      email: lead.email || '',
      source: lead.crm_source || lead.source?.id || '',
      lifeStage: lead.crm_life_stage || lead.life_stage?.id || '',
      assignedTo: Array.isArray(lead.lead_users) ? lead.lead_users.map(u => u.id || u.user_id) : []
    });
    fetchCrmCreateResources();
    setShowAddLeadModal(true);
  };

  const handleSaveLead = async () => {
    if (!leadForm.firstName.trim()) {
      toast.warning('First name is required.');
      return;
    }
    if (!leadForm.mobile.trim()) {
      toast.warning('Mobile number is required.');
      return;
    }
    if (!leadForm.assignedTo.length) {
      toast.warning('Please assign at least one user.');
      return;
    }

    const payload = {
      type: leadForm.contactType.toLowerCase(),
      first_name: leadForm.firstName,
      last_name: leadForm.lastName,
      contact_id: leadForm.contactId,
      mobile: leadForm.mobile,
      alternate_number: leadForm.alternate,
      landline: leadForm.landline,
      email: leadForm.email,
      user_id: leadForm.assignedTo,
      supplier_business_name: leadForm.contactKind === 'Business' ? leadForm.firstName : ''
    };

    if (leadForm.source) payload.crm_source = leadForm.source;
    if (leadForm.lifeStage) payload.crm_life_stage = leadForm.lifeStage;

    try {
      if (editingLeadId) {
        await apiService.updateCrmLead(editingLeadId, payload);
        toast.success('Lead updated successfully!');
      } else {
        await apiService.createCrmLead(payload);
        toast.success('Lead added successfully!');
      }
      setShowAddLeadModal(false);
      setEditingLeadId(null);
      setLeadForm({
        contactType: 'Lead', contactKind: 'Individual', contactId: '',
        customerGroup: '', firstName: '', lastName: '', mobile: '', alternate: '', landline: '', email: '',
        source: '', lifeStage: '', assignedTo: []
      });
      fetchLeads();
    } catch (error) {
      /* console.error('Failed to save lead:', error); */
      toast.error('Failed to save lead. Please try again.');
    }
  };

  const handleSaveFollowup = async () => {
    if (!followupForm.title.trim()) {
      toast.warning('Title is required.');
      return;
    }
    if (!followupForm.customer || String(followupForm.customer) === 'undefined' || String(followupForm.customer) === 'null') {
      toast.warning('Customer/Lead is required.');
      return;
    }
    if (!followupForm.startDatetime || !followupForm.endDatetime) {
      toast.warning('Start and end datetime are required.');
      return;
    }

    try {
      try {
        await apiService.getLoggedInUser();
      } catch (e) {
      /* console.warn('Could not refresh business settings:', e); */
      }

      const payload = {
        title: followupForm.title,
        contact_id: followupForm.customer,
        status: followupForm.status,
        start_datetime: formatDatetimeForBusinessUfDate(followupForm.startDatetime),
        end_datetime: formatDatetimeForBusinessUfDate(followupForm.endDatetime),
        description: followupForm.description,
        schedule_type: followupForm.followupType,
        followup_category_id: followupForm.followupCategory,
        user_id: followupForm.assignedTo,
        notify_type: followupForm.sendNotification ? 'email' : null,
        is_recursive: followupSubTab === 'recurring' ? 1 : 0
      };

      if (editingFollowupId) {
        await apiService.updateCrmFollowUp(editingFollowupId, payload);
        toast.success('Follow up updated successfully!');
      } else {
        await apiService.createCrmFollowUp(payload);
        toast.success('Follow up saved successfully!');
      }
      setShowAddFollowupModal(false);
      setEditingFollowupId(null);
      setFollowupForm({
        title: '', customer: '', status: '', startDatetime: '', endDatetime: '',
        description: '', followupType: '', followupCategory: '', assignedTo: [], sendNotification: false
      });
      fetchFollowUps(followupSubTab);
    } catch (error) {
      /* console.error('Failed to save follow-up:', error); */
      toast.error('Failed to save follow-up. Please try again.');
    }
  };

  const handleSaveCampaign = async () => {
    if (!campaignForm.name.trim()) {
      toast.warning('Campaign name is required.');
      return;
    }
    if (!campaignForm.type) {
      toast.warning('Campaign type is required.');
      return;
    }
    if (!campaignForm.subject.trim()) {
      toast.warning('Subject is required.');
      return;
    }
    if (!campaignForm.body.trim()) {
      toast.warning('Body is required.');
      return;
    }

    const payload = {
      name: campaignForm.name,
      campaign_type: campaignForm.type.toLowerCase(),
      subject: campaignForm.subject,
      email_body: campaignForm.type === 'Email' ? campaignForm.body : '',
      sms_body: campaignForm.type === 'SMS' ? campaignForm.body : '',
      to: campaignForm.to,
      contact_id: [],
      lead_id: [],
      contact: []
    };

    try {
      await apiService.createCrmCampaign(payload);
      toast.success('Campaign saved successfully!');
      setShowAddCampaignModal(false);
      setCampaignForm({ name: '', type: 'Email', to: '', subject: '', body: '' });
      fetchCampaigns();
    } catch (error) {
      /* console.error('Failed to save campaign:', error); */
      toast.error('Failed to save campaign. Please try again.');
    }
  };

  const handleSaveSource = async () => {
    if (!sourceForm.name.trim()) {
      toast.warning('Source name is required.');
      return;
    }
    try {
      await apiService.createCategory({
        name: sourceForm.name,
        description: sourceForm.description,
        category_type: 'crm_source'
      });
      toast.success('Source saved successfully!');
      setShowAddSourceModal(false);
      setSourceForm({ name: '', description: '' });
      fetchSources();
    } catch (error) {
      /* console.error('Failed to save source:', error); */
      toast.error('Failed to save source. Please try again.');
    }
  };

  const handleSaveLifeStage = async () => {
    if (!lifeStageForm.name.trim()) {
      toast.warning('Life stage name is required.');
      return;
    }
    try {
      await apiService.createCategory({
        name: lifeStageForm.name,
        description: lifeStageForm.description,
        category_type: 'life_stage'
      });
      toast.success('Life stage saved successfully!');
      setShowAddLifeStageModal(false);
      setLifeStageForm({ name: '', description: '' });
      fetchLifeStages();
    } catch (error) {
      /* console.error('Failed to save life stage:', error); */
      toast.error('Failed to save life stage. Please try again.');
    }
  };

  const handleSaveFollowupCategory = async () => {
    if (!followupCategoryForm.name.trim()) {
      toast.warning('Followup category name is required.');
      return;
    }
    try {
      await apiService.createCategory({
        name: followupCategoryForm.name,
        description: followupCategoryForm.description,
        category_type: 'followup_category'
      });
      toast.success('Followup category saved successfully!');
      setShowAddFollowupCategoryModal(false);
      setFollowupCategoryForm({ name: '', description: '' });
      fetchFollowupCategories();
    } catch (error) {
      /* console.error('Failed to save followup category:', error); */
      toast.error('Failed to save followup category. Please try again.');
    }
  };

  const handleEditCategory = async (id, currentName, currentDesc, fetchFn) => {
    const newName = window.prompt('Category Name:', currentName);
    if (newName === null) return;
    const newDesc = window.prompt('Description:', currentDesc || '');
    if (newDesc === null) return;
    try {
      await apiService.updateCategory(id, { name: newName, description: newDesc });
      toast.success('Category updated successfully.');
      fetchFn();
    } catch (e) {
      /* console.error(e); */
      toast.error('Failed to update category.');
    }
  };

  const handleDeleteCategory = async (id, fetchFn) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await apiService.deleteCategory(id);
      toast.success('Category deleted successfully.');
      fetchFn();
    } catch (e) {
      /* console.error(e); */
      toast.error('Failed to delete category.');
    }
  };

  const handleSaveCrmSettings = async () => {
    try {
      await apiService.updateCrmSettings({
        enable_order_request: crmSettingsData.enable_order_request ? 1 : 0,
        order_request_prefix: crmSettingsData.order_request_prefix
      });
      toast.success('CRM settings saved successfully.');
      fetchCrmSettings();
    } catch (error) {
      /* console.error('Failed to save CRM settings:', error); */
      toast.error('Failed to save CRM settings. Please try again.');
    }
  };

  const handleSaveContactLogin = async () => {
    if (!contactLoginForm.contact || !contactLoginForm.email) {
      toast.warning('Contact and Email are required.');
      return;
    }
    try {
      const payload = {
        crm_contact_id: contactLoginForm.contact,
        email: contactLoginForm.email,
        contact_no: contactLoginForm.mobile,
        alt_number: contactLoginForm.alternate,
        family_number: contactLoginForm.familyContact,
        crm_department: contactLoginForm.department,
        crm_designation: contactLoginForm.designation,
        cmmsn_percent: contactLoginForm.commission,
        is_active: contactLoginForm.isActive ? 1 : 0,
        allow_login: contactLoginForm.allowLogin ? 1 : 0
      };
      if (editingContactLoginId) {
        await apiService.updateCrmContactLogin(editingContactLoginId, payload);
        toast.success('Contact login updated successfully.');
      } else {
        await apiService.createCrmContactLogin(payload);
        toast.success('Contact login saved successfully.');
      }
      setShowAddContactLoginModal(false);
      setEditingContactLoginId(null);
      setContactLoginForm({
        contact: '', email: '', mobile: '', alternate: '', familyContact: '',
        department: '', designation: '', commission: '', isActive: true, allowLogin: true
      });
      fetchContactsLogin();
    } catch (error) {
      /* console.error('Failed to save contact login:', error); */
      toast.error('Failed to save contact login. Please try again.');
    }
  };

  const handleEditContactLogin = async (c) => {
    const userId = c.id || c.user_id || (c.action && c.action.match(/\/contact-login\/(\d+)/)?.[1]);
    const crmContactId = c.crm_contact_id || c.contact_id || (c.action && c.action.match(/crm_contact_id=(\d+)/)?.[1]) || '';
    setEditingContactLoginId(userId);
    setOpenContactLoginActionId(null);
    setContactLoginForm({
      contact: String(crmContactId),
      email: c.email || '',
      mobile: c.contact_no || c.mobile || '',
      alternate: c.alt_number || c.alternate || '',
      familyContact: c.family_number || c.familyContact || '',
      department: c.crm_department || c.department || '',
      designation: c.crm_designation || c.designation || '',
      commission: c.cmmsn_percent || c.commission || '',
      isActive: c.status ? (c.status === 'active') : true,
      allowLogin: c.allow_login !== undefined ? Boolean(c.allow_login) : true
    });
    fetchContactLoginResources();
    setShowAddContactLoginModal(true);

    if (userId) {
      try {
        const res = await apiService.getCrmContactLoginForEdit(userId, crmContactId);
        const u = res?.user || res?.data?.user || res?.data;
        if (u) {
          setContactLoginForm({
            contact: String(u.crm_contact_id || crmContactId),
            email: u.email || '',
            mobile: u.contact_no || '',
            alternate: u.alt_number || '',
            familyContact: u.family_number || '',
            department: u.crm_department || '',
            designation: u.crm_designation || '',
            commission: u.cmmsn_percent || '',
            isActive: u.status === 'active',
            allowLogin: u.allow_login === 1 || u.allow_login === true
          });
        }
      } catch (e) {
      /* console.error('Error fetching details for edit:', e); */
      }
    }
  };

  const handleDeleteContactLogin = async (c) => {
    const userId = c.id || c.user_id || (c.action && c.action.match(/\/contact-login\/(\d+)/)?.[1]);
    const crmContactId = c.crm_contact_id || c.contact_id || (c.action && c.action.match(/crm_contact_id=(\d+)/)?.[1]) || '';
    if (!userId) {
      toast.error('Could not determine user ID for deletion.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this contact login?')) return;
    try {
      await apiService.deleteCrmContactLogin(userId, crmContactId);
      toast.success('Contact login deleted successfully.');
      fetchContactsLogin();
    } catch (error) {
      /* console.error('Failed to delete contact login:', error); */
      alert('Failed to delete contact login. Please try again.');
    } finally {
      setOpenContactLoginActionId(null);
    }
  };

  const ExportDropdown = ({ show, setShow }) => (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setShow(!show)}
        style={{ background: 'var(--etpos-card)', color: 'var(--etpos-text-muted)', border: '1px solid var(--etpos-border)', padding: '0.4rem 0.9rem', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>
        <Download size={15} /> Export <ChevronDown size={13} />
      </button>
      {show && (
        <div style={{ position: 'absolute', top: '110%', right: 0, background: 'var(--etpos-card)', border: '1px solid var(--etpos-border)', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, minWidth: '170px', padding: '0.4rem 0' }}>
          {[
            { label: 'Export CSV', icon: <FileSpreadsheet size={14} color="#10b981" /> },
            { label: 'Export Excel', icon: <FileSpreadsheet size={14} color="#3b82f6" /> },
            { label: 'Print', icon: <Printer size={14} color="#6366f1" /> },
            { label: 'Export PDF', icon: <FileText size={14} color="#ef4444" /> },
          ].map((opt, i) => (
            <button key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.83rem', color: 'var(--etpos-text)', cursor: 'pointer' }}>
              {opt.icon} {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderAddLeadModal = () => (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', width: '780px', maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text)' }}>Add a new contact</h3>
          <button onClick={() => setShowAddLeadModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: 'var(--etpos-text-muted)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>
        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', alignItems: 'start' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Contact type:*</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden' }}>
                <span style={{ padding: '0.5rem 0.65rem', background: 'var(--etpos-bg)', borderRight: '1px solid var(--etpos-border)', color: 'var(--etpos-text-muted)' }}><Users size={15} /></span>
                <select value={leadForm.contactType} onChange={e => setLeadForm({ ...leadForm, contactType: e.target.value })} style={{ flex: 1, padding: '0.5rem 0.65rem', border: 'none', outline: 'none', fontSize: '0.875rem', background: 'transparent' }}>
                  <option>Lead</option><option>Customer</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', paddingTop: '1.7rem' }}>
              {['Individual', 'Business'].map(kind => (
                <label key={kind} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.875rem', color: 'var(--etpos-text)', cursor: 'pointer' }}>
                  <input type="radio" name="contactKind" value={kind} checked={leadForm.contactKind === kind} onChange={e => setLeadForm({ ...leadForm, contactKind: e.target.value })} /> {kind}
                </label>
              ))}
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Contact ID:</label>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden' }}>
                <span style={{ padding: '0.5rem 0.65rem', background: 'var(--etpos-bg)', borderRight: '1px solid var(--etpos-border)', color: 'var(--etpos-text-muted)' }}><FileText size={15} /></span>
                <input type="text" placeholder="Contact ID" value={leadForm.contactId} onChange={e => setLeadForm({ ...leadForm, contactId: e.target.value })} style={{ flex: 1, padding: '0.5rem 0.65rem', border: 'none', outline: 'none', fontSize: '0.875rem' }} />
              </div>
              <small style={{ color: 'var(--etpos-text-muted)', fontSize: '0.78rem' }}>Leave empty to autogenerate</small>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>First Name:*</label>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden' }}>
                <input type="text" placeholder="First Name" value={leadForm.firstName} onChange={e => setLeadForm({ ...leadForm, firstName: e.target.value })} style={{ flex: 1, padding: '0.5rem 0.65rem', border: 'none', outline: 'none', fontSize: '0.875rem' }} />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Last Name:</label>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden' }}>
                <input type="text" placeholder="Last Name" value={leadForm.lastName} onChange={e => setLeadForm({ ...leadForm, lastName: e.target.value })} style={{ flex: 1, padding: '0.5rem 0.65rem', border: 'none', outline: 'none', fontSize: '0.875rem' }} />
              </div>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Customer Group:</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden' }}>
              <span style={{ padding: '0.5rem 0.65rem', background: 'var(--etpos-bg)', borderRight: '1px solid var(--etpos-border)', color: 'var(--etpos-text-muted)' }}><Users size={15} /></span>
              <select value={leadForm.customerGroup} onChange={e => setLeadForm({ ...leadForm, customerGroup: e.target.value })} style={{ flex: 1, padding: '0.5rem 0.65rem', border: 'none', outline: 'none', fontSize: '0.875rem', background: 'transparent' }}>
                <option value=""></option><option>Group A</option><option>Group B</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: '1rem' }}>
            {[
              { label: 'Mobile:*', placeholder: 'Mobile', key: 'mobile', icon: <Phone size={14} /> },
              { label: 'Alternate number:', placeholder: 'Alternate', key: 'alternate', icon: <Phone size={14} /> },
              { label: 'Landline:', placeholder: 'Landline', key: 'landline', icon: <Phone size={14} /> },
              { label: 'Email:', placeholder: 'Email', key: 'email', icon: <Mail size={14} /> },
            ].map(field => (
              <div key={field.key}>
                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>{field.label}</label>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden' }}>
                  <span style={{ padding: '0.5rem 0.55rem', background: 'var(--etpos-bg)', borderRight: '1px solid var(--etpos-border)', color: 'var(--etpos-text-muted)' }}>{field.icon}</span>
                  <input type="text" placeholder={field.placeholder} value={leadForm[field.key]} onChange={e => setLeadForm({ ...leadForm, [field.key]: e.target.value })} style={{ flex: 1, padding: '0.5rem 0.6rem', border: 'none', outline: 'none', fontSize: '0.875rem' }} />
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Source:</label>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden' }}>
                <span style={{ padding: '0.5rem 0.65rem', background: 'var(--etpos-bg)', borderRight: '1px solid var(--etpos-border)', color: 'var(--etpos-text-muted)' }}><TrendingUp size={14} /></span>
                <select value={leadForm.source} onChange={e => setLeadForm({ ...leadForm, source: e.target.value })} style={{ flex: 1, padding: '0.5rem 0.65rem', border: 'none', outline: 'none', fontSize: '0.875rem', background: 'transparent' }}>
                  <option value="">Please Select</option>
                  {crmResources.sources.map((s, i) => <option key={i} value={s.id || s}>{s.name || s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Life Stage:</label>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden' }}>
                <span style={{ padding: '0.5rem 0.65rem', background: 'var(--etpos-bg)', borderRight: '1px solid var(--etpos-border)', color: 'var(--etpos-text-muted)' }}><Heart size={14} /></span>
                <select value={leadForm.lifeStage} onChange={e => setLeadForm({ ...leadForm, lifeStage: e.target.value })} style={{ flex: 1, padding: '0.5rem 0.65rem', border: 'none', outline: 'none', fontSize: '0.875rem', background: 'transparent' }}>
                  <option value="">Please Select</option>
                  {crmResources.lifeStages.map((s, i) => <option key={i} value={s.id || s}>{s.name || s}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Assigned to:*</label>
            <div style={{ display: 'flex', alignItems: 'flex-start', border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden', minHeight: '100px' }}>
              <span style={{ padding: '0.65rem', background: 'var(--etpos-bg)', borderRight: '1px solid var(--etpos-border)', color: 'var(--etpos-text-muted)', alignSelf: 'stretch', display: 'flex', alignItems: 'flex-start', paddingTop: '0.9rem' }}><Users size={15} /></span>
              <div style={{ flex: 1, padding: '0.65rem 0.75rem' }}>
                {crmResources.users.map((user, i) => {
                  const userId = user.id || user;
                  const userName = user.name || user;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.35rem' }}>
                      <input
                        type="checkbox"
                        id={`assign-${i}`}
                        checked={leadForm.assignedTo.includes(String(userId))}
                        onChange={e => {
                          const updated = e.target.checked
                            ? [...leadForm.assignedTo, String(userId)]
                            : leadForm.assignedTo.filter(p => p !== String(userId));
                          setLeadForm({ ...leadForm, assignedTo: updated });
                        }}
                      />
                      <label htmlFor={`assign-${i}`} style={{ fontSize: '0.875rem', color: 'var(--etpos-text)', cursor: 'pointer' }}>{userName}</label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button onClick={handleSaveLead} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer' }}>Save</button>
          <button onClick={() => setShowAddLeadModal(false)} style={{ background: 'var(--etpos-text)', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );

  const renderAddCampaignModal = () => (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', width: '750px', maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: 'var(--etpos-text)' }}>Add Campaign</h3>
          <button onClick={() => setShowAddCampaignModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: 'var(--etpos-text-muted)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>
        <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Campaign Name:*</label>
              <input type="text" placeholder="Campaign Name" value={campaignForm.name} onChange={e => setCampaignForm({ ...campaignForm, name: e.target.value })} style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.95rem', background: 'var(--etpos-bg)', color: 'var(--etpos-text)' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Campaign Type:*</label>
              <select value={campaignForm.type} onChange={e => setCampaignForm({ ...campaignForm, type: e.target.value })} style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.95rem', background: 'var(--etpos-bg)', color: 'var(--etpos-text)' }}>
                <option value="Email">Email</option>
                <option value="SMS">SMS</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>To:*</label>
              <select value={campaignForm.to} onChange={e => setCampaignForm({ ...campaignForm, to: e.target.value })} style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.95rem', background: 'var(--etpos-bg)', color: 'var(--etpos-text)' }}>
                <option value="">Please Select</option>
                <option value="all">All Contacts</option>
                <option value="leads">Leads Only</option>
                <option value="customers">Customers Only</option>
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Subject:*</label>
              <input type="text" placeholder="Subject" value={campaignForm.subject} onChange={e => setCampaignForm({ ...campaignForm, subject: e.target.value })} style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.95rem', background: 'var(--etpos-bg)', color: 'var(--etpos-text)' }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Email Body:*</label>
              <div style={{ border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden' }}>
                <Editor
                  init={{
                    height: 300,
                    menubar: false,
                    plugins: [
                      'advlist autolink lists link image charmap print preview anchor',
                      'searchreplace visualblocks code fullscreen',
                      'insertdatetime media table paste code help wordcount'
                    ],
                    toolbar: 'undo redo | formatselect | ' +
                      'bold italic backcolor | alignleft aligncenter ' +
                      'alignright alignjustify | bullist numlist outdent indent | ' +
                      'removeformat | help',
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                  }}
                  value={campaignForm.body}
                  onEditorChange={(content) => setCampaignForm({ ...campaignForm, body: content })}
                />
              </div>
              <div style={{ marginTop: '0.5rem', textAlign: 'right' }}>
                <small style={{ color: 'var(--etpos-text-muted, #94a3b8)', fontSize: '0.7rem' }}>Powered by Tiny</small>
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600' }}>Available Tags:</p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {['{contact_name}', '{campaign_name}', '{business_name}'].map(tag => (
                  <button key={tag} onClick={() => setCampaignForm({ ...campaignForm, body: campaignForm.body + tag })} style={{ padding: '0.3rem 0.6rem', background: 'var(--etpos-bg, #f1f5f9)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '4px', fontSize: '0.75rem', color: '#4f46e5', cursor: 'pointer' }}>{tag}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: '1.25rem 2rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <button onClick={handleSaveCampaign} style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.75rem 2.5rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Save</button>
          <button onClick={() => setShowAddCampaignModal(false)} style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.75rem 2.5rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );

  const renderCampaignsTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem' }}>
          <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
            <div className="table-controls-left">
              <div className="entries-dropdown">
                <span>Show</span>
                <select value={campaignsEntries} onChange={e => setCampaignsEntries(e.target.value)}>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
            </div>
            <div className="table-controls-right">
              <div className="tabs-search-wrapper">
                <input type="text" value={campaignsSearch} onChange={e => setCampaignsSearch(e.target.value)} placeholder="Search..." className="tabs-search-input" />
              </div>

              <ExportDropdown show={showCampaignExportDropdown} setShow={setShowCampaignExportDropdown} />

              <button className="control-btn-primary" onClick={() => setShowAddCampaignModal(true)}>
                <span className="plus-icon">+</span> Add
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                  {['Action', 'Campaign Name', 'Campaign Type', 'Created By', 'Created At'].map((h, i) => (
                    <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crmLoading ? (
                  <tr><td colSpan={5} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                ) : campaignsData.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                        <Megaphone size={32} opacity={0.2} />
                        No data available in table
                      </div>
                    </td>
                  </tr>
                ) : (
                  campaignsData.map((c, i) => (
                    <tr key={c.id || i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                          <button onClick={() => handleSendCampaignNotification(c.id)} title="Send Notification" style={{ padding: '0.25rem 0.55rem', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>Send</button>
                          <button onClick={() => handleDeleteCampaign(c.id)} title="Delete Campaign" style={{ padding: '0.25rem 0.55rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>Delete</button>
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>{c.name || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{c.campaign_type || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{c.created_by?.user_full_name || c.created_by_name || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{c.created_at || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {campaignsData.length > 0 ? 1 : 0} to {campaignsData.length} of {campaignsData.length} entries</span>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAddFollowupModal = () => {
    const rawCustomers = followUpResources.contacts || followUpResources.customers;
    const customers = rawCustomers && Object.keys(rawCustomers).length > 0
      ? normalizeOptions(rawCustomers)
      : (leadsData || []).map(l => ({ id: l.id || l.contact_id, name: l.supplier_business_name ? `${l.supplier_business_name} (${l.name || ''})` : (l.name || `${l.first_name || ''} ${l.last_name || ''}`) }));

    const rawStatuses = followUpResources.statuses;
    const statuses = rawStatuses && Object.keys(rawStatuses).length > 0
      ? normalizeOptions(rawStatuses)
      : [
        { id: 'scheduled', name: 'Scheduled' },
        { id: 'open', name: 'Open' },
        { id: 'completed', name: 'Completed' },
        { id: 'cancelled', name: 'Cancelled' }
      ];

    const rawTypes = followUpResources.follow_up_types || followUpResources.schedule_type;
    const followUpTypes = rawTypes && Object.keys(rawTypes).length > 0
      ? normalizeOptions(rawTypes)
      : [
        { id: 'Call', name: 'Call' },
        { id: 'SMS', name: 'SMS' },
        { id: 'Meeting', name: 'Meeting' },
        { id: 'Email', name: 'Email' }
      ];

    const rawCategories = followUpResources.followup_category || followUpResources.categories;
    const categories = rawCategories && Object.keys(rawCategories).length > 0
      ? normalizeOptions(rawCategories)
      : (followupCategoriesData || []).map(c => ({ id: c.id, name: c.name }));

    const rawUsers = followUpResources.assigned_to || followUpResources.users;
    const users = rawUsers && Object.keys(rawUsers).length > 0
      ? normalizeOptions(rawUsers)
      : normalizeOptions(crmResources.users);

    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', width: '700px', maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
          <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.11rem', fontWeight: '700', color: 'var(--etpos-text)' }}>Add Follow up</h3>
            <button onClick={() => setShowAddFollowupModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: 'var(--etpos-text-muted)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
          </div>
          <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
              <div style={{ gridColumn: '1 / -1' }}><label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Title:*</label><input type="text" placeholder="Title" value={followupForm.title} onChange={e => setFollowupForm({ ...followupForm, title: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} /></div>
              <div><label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Customer/Lead:*</label><select value={followupForm.customer} onChange={e => setFollowupForm({ ...followupForm, customer: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }}><option value="">Please Select</option>{customers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</select></div>
              <div><label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Status:</label><select value={followupForm.status} onChange={e => setFollowupForm({ ...followupForm, status: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }}><option value="">Please Select</option>{statuses.map((s, i) => <option key={i} value={s.id}>{s.name}</option>)}</select></div>
              <div><label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Start Datetime:*</label><input type="datetime-local" value={followupForm.startDatetime} onChange={e => setFollowupForm({ ...followupForm, startDatetime: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} /></div>
              <div><label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>End Datetime:*</label><input type="datetime-local" value={followupForm.endDatetime} onChange={e => setFollowupForm({ ...followupForm, endDatetime: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Description:</label><textarea placeholder="Description" value={followupForm.description} onChange={e => setFollowupForm({ ...followupForm, description: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem', minHeight: '80px' }} /></div>
              <div><label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Follow Up Type:*</label><select value={followupForm.followupType} onChange={e => setFollowupForm({ ...followupForm, followupType: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }}><option value="">Please Select</option>{followUpTypes.map((t, i) => <option key={i} value={t.id}>{t.name}</option>)}</select></div>
              <div><label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Followup Category:*</label><select value={followupForm.followupCategory} onChange={e => setFollowupForm({ ...followupForm, followupCategory: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }}><option value="">Please Select</option>{categories.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}</select></div>
            </div>
            <div><label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Assigned to:*</label>
              <div style={{ display: 'flex', alignItems: 'flex-start', border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden', minHeight: '80px' }}>
                <span style={{ padding: '0.65rem', background: 'var(--etpos-bg)', borderRight: '1px solid var(--etpos-border)', color: 'var(--etpos-text-muted)' }}><Users size={15} /></span>
                <div style={{ flex: 1, padding: '0.65rem 0.75rem' }}>
                  {users.map((user, i) => {
                    const userId = String(user.id);
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                        <input type="checkbox" id={`f-assign-${i}`} checked={followupForm.assignedTo.includes(userId)} onChange={e => {
                          const updated = e.target.checked
                            ? [...followupForm.assignedTo, userId]
                            : followupForm.assignedTo.filter(p => p !== userId);
                          setFollowupForm({ ...followupForm, assignedTo: updated });
                        }} />
                        <label htmlFor={`f-assign-${i}`} style={{ fontSize: '0.875rem' }}>{user.name}</label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" id="sn" checked={followupForm.sendNotification} onChange={e => setFollowupForm({ ...followupForm, sendNotification: e.target.checked })} /> <label htmlFor="sn" style={{ fontSize: '0.875rem', fontWeight: '600' }}>Send Notification</label></div>
          </div>
          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button onClick={handleSaveFollowup} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '700', cursor: 'pointer' }}>Save</button>
            <button onClick={() => setShowAddFollowupModal(false)} style={{ background: 'var(--etpos-text)', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2rem', fontWeight: '700', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const renderFollowupFiltersModal = () => {
    const customers = normalizeOptions(followUpResources.contacts || followUpResources.customers);
    const statuses = normalizeOptions(followUpResources.statuses);
    const followUpTypes = normalizeOptions(followUpResources.follow_up_types);
    const categories = normalizeOptions(followUpResources.followup_category);
    const users = normalizeOptions(followUpResources.assigned_to || followUpResources.users);
    return (
      <div className="modal-overlay" onClick={() => setShowFollowupFilters(false)}>
        <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%' }}>
          <div className="modal-header">
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
            <button className="modal-close-btn" onClick={() => setShowFollowupFilters(false)}>✕</button>
          </div>
          <div className="modal-content" style={{ padding: '1.5rem' }}>
            <div className="filter-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem' }}>
              <div className="crm-form-group">
                <label className="crm-form-label">Contact</label>
                <select value={followupFilters.contact} onChange={e => setFollowupFilters({ ...followupFilters, contact: e.target.value })}>
                  <option value="">All</option>
                  {customers.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="crm-form-group">
                <label className="crm-form-label">Assigned to</label>
                <select value={followupFilters.assigned_to} onChange={e => setFollowupFilters({ ...followupFilters, assigned_to: e.target.value })}>
                  <option value="">All</option>
                  {users.map((u, i) => <option key={i} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="crm-form-group">
                <label className="crm-form-label">Status</label>
                <select value={followupFilters.status} onChange={e => setFollowupFilters({ ...followupFilters, status: e.target.value })}>
                  <option value="">All</option>
                  {statuses.map((s, i) => <option key={i} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="crm-form-group">
                <label className="crm-form-label">Schedule Type</label>
                <select value={followupFilters.schedule_type} onChange={e => setFollowupFilters({ ...followupFilters, schedule_type: e.target.value })}>
                  <option value="">All</option>
                  {followUpTypes.map((t, i) => <option key={i} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="crm-form-group">
                <label className="crm-form-label">Followup Category</label>
                <select value={followupFilters.followup_category} onChange={e => setFollowupFilters({ ...followupFilters, followup_category: e.target.value })}>
                  <option value="">All</option>
                  {categories.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="crm-form-group">
                <label className="crm-form-label">Date Range</label>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--etpos-border)', borderRadius: '4px', padding: '0.45rem 0.75rem', background: 'var(--etpos-bg)' }}>
                  <Calendar size={15} style={{ marginRight: '0.5rem' }} />
                  <span style={{ fontSize: '0.85rem' }}>{dateRange}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', alignItems: 'center' }}>
            <button onClick={() => setFollowupFilters({ contact: '', assigned_to: '', status: '', schedule_type: '', followup_category: '', date_range: '' })} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', height: '38px', padding: '0 1.25rem', background: '#0f172a', color: '#ffffff', border: '1px solid #0f172a', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', boxSizing: 'border-box' }}>
              <RotateCcw size={14} /> Reset
            </button>
            <button className="modal-btn close-btn" onClick={() => setShowFollowupFilters(false)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '38px', padding: '0 1.25rem', background: 'var(--etpos-card, #ffffff)', color: 'var(--etpos-text, #334155)', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', boxSizing: 'border-box' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLeadsFiltersModal = () => {
    return (
      <div className="modal-overlay" onClick={() => setShowLeadsFilters(false)}>
        <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%' }}>
          <div className="modal-header">
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
            <button className="modal-close-btn" onClick={() => setShowLeadsFilters(false)}>✕</button>
          </div>
          <div className="modal-content" style={{ padding: '1.5rem' }}>
            <div className="filter-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem' }}>
              <div className="crm-form-group">
                <label className="crm-form-label">Source</label>
                <select value={leadsSourceFilter} onChange={e => setLeadsSourceFilter(e.target.value)}>
                  <option value="">All</option>
                  {crmResources.sources.map((s, i) => <option key={i} value={s.id || s}>{s.name || s}</option>)}
                </select>
              </div>
              <div className="crm-form-group">
                <label className="crm-form-label">Life Stage</label>
                <select value={leadsLifeStageFilter} onChange={e => setLeadsLifeStageFilter(e.target.value)}>
                  <option value="">All</option>
                  {crmResources.lifeStages.map((s, i) => <option key={i} value={s.id || s}>{s.name || s}</option>)}
                </select>
              </div>
              <div className="crm-form-group">
                <label className="crm-form-label">Assigned to</label>
                <select value={leadsAssignedToFilter} onChange={e => setLeadsAssignedToFilter(e.target.value)}>
                  <option value="">All</option>
                  {crmResources.users.map((u, i) => <option key={i} value={u.id || u}>{u.name || u}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', alignItems: 'center' }}>
            <button onClick={() => { setLeadsSourceFilter(''); setLeadsLifeStageFilter(''); setLeadsAssignedToFilter(''); }} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', height: '38px', padding: '0 1.25rem', background: '#0f172a', color: '#ffffff', border: '1px solid #0f172a', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', boxSizing: 'border-box' }}>
              <RotateCcw size={14} /> Reset
            </button>
            <button className="modal-btn close-btn" onClick={() => setShowLeadsFilters(false)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '38px', padding: '0 1.25rem', background: 'var(--etpos-card, #ffffff)', color: 'var(--etpos-text, #334155)', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', boxSizing: 'border-box' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAddContactLoginModal = () => {
    const contacts = normalizeOptions(contactLoginResources);
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', width: '700px', maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
          <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text)' }}>{editingContactLoginId ? 'Edit Contact Login' : 'Add Contact Login'}</h3>
            <button onClick={() => { setShowAddContactLoginModal(false); setEditingContactLoginId(null); }} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: 'var(--etpos-text-muted)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
          </div>
          <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Contact:*</label>
                <select value={contactLoginForm.contact} onChange={e => setContactLoginForm({ ...contactLoginForm, contact: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }}>
                  <option value="">Please Select</option>
                  {contacts.map((c, i) => <option key={i} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Email:*</label>
                <input type="email" placeholder="Email" value={contactLoginForm.email} onChange={e => setContactLoginForm({ ...contactLoginForm, email: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Mobile Number:</label>
                <input type="text" placeholder="Mobile Number" value={contactLoginForm.mobile} onChange={e => setContactLoginForm({ ...contactLoginForm, mobile: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Alternate contact number:</label>
                <input type="text" placeholder="Alternate contact number" value={contactLoginForm.alternate} onChange={e => setContactLoginForm({ ...contactLoginForm, alternate: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Family contact number:</label>
                <input type="text" placeholder="Family contact number" value={contactLoginForm.familyContact} onChange={e => setContactLoginForm({ ...contactLoginForm, familyContact: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Department:</label>
                <input type="text" placeholder="Department" value={contactLoginForm.department} onChange={e => setContactLoginForm({ ...contactLoginForm, department: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Designation:</label>
                <input type="text" placeholder="Designation" value={contactLoginForm.designation} onChange={e => setContactLoginForm({ ...contactLoginForm, designation: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Sales Commission Percentage (%):</label>
                <input type="number" placeholder="Sales Commission Percentage (%)" value={contactLoginForm.commission} onChange={e => setContactLoginForm({ ...contactLoginForm, commission: e.target.value })} style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600' }}>
                <input type="checkbox" checked={contactLoginForm.isActive} onChange={e => setContactLoginForm({ ...contactLoginForm, isActive: e.target.checked })} /> Is active ?
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600' }}>
                <input type="checkbox" checked={contactLoginForm.allowLogin} onChange={e => setContactLoginForm({ ...contactLoginForm, allowLogin: e.target.checked })} /> Allow login
              </label>
            </div>
          </div>
          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
            <button onClick={handleSaveContactLogin} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2.5rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Save</button>
            <button onClick={() => { setShowAddContactLoginModal(false); setEditingContactLoginId(null); }} style={{ background: 'var(--etpos-text)', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2.5rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const renderContactLoginFiltersModal = () => (
    <div className="modal-overlay" onClick={() => setShowContactLoginFilters(false)}>
      <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%' }}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
          <button className="modal-close-btn" onClick={() => setShowContactLoginFilters(false)}>✕</button>
        </div>
        <div className="modal-content" style={{ padding: '1.5rem' }}>
          <div className="filter-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem' }}>
            <div className="filter-group">
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: 'var(--etpos-text-muted)', marginBottom: '0.4rem' }}>Department:</label>
              <select style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.85rem' }}>
                <option>All</option>
              </select>
            </div>
            <div className="filter-group">
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: 'var(--etpos-text-muted)', marginBottom: '0.4rem' }}>Designation:</label>
              <select style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.85rem' }}>
                <option>All</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', alignItems: 'center' }}>
          <button style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', height: '38px', padding: '0 1.25rem', background: '#0f172a', color: '#ffffff', border: '1px solid #0f172a', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', boxSizing: 'border-box' }}><RotateCcw size={14} /> Reset</button>
          <button className="modal-btn close-btn" onClick={() => setShowContactLoginFilters(false)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '38px', padding: '0 1.25rem', background: 'var(--etpos-card, #ffffff)', color: 'var(--etpos-text, #334155)', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer', boxSizing: 'border-box' }}>Close</button>
        </div>
      </div>
    </div>
  );

  const renderContactsLoginTab = () => {
    const filteredContactsLoginData = contactsLoginData.filter(c => {
      if (!contactLoginSearch) return true;
      const q = contactLoginSearch.toLowerCase();
      const contactName = (c.contact || c.contact_name || '').toLowerCase();
      const username = (c.username || '').toLowerCase();
      const name = (c.name || [c.surname, c.first_name, c.last_name].filter(Boolean).join(' ') || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      const dept = (c.crm_department || c.department || '').toLowerCase();
      const desig = (c.crm_designation || c.designation || '').toLowerCase();
      return contactName.includes(q) || username.includes(q) || name.includes(q) || email.includes(q) || dept.includes(q) || desig.includes(q);
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem' }}>
            <div className="crm-sub-tabs" style={{ borderBottom: '1px solid var(--etpos-border)', marginBottom: '1.5rem', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
              <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                <button onClick={() => setActiveTab('contacts_login')} style={{ background: 'none', border: 'none', padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: activeTab === 'contacts_login' ? '700' : '500', color: activeTab === 'contacts_login' ? 'var(--etpos-text)' : 'var(--etpos-text-muted)', borderBottom: activeTab === 'contacts_login' ? '2px solid #6366f1' : '2px solid transparent', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>Contacts Login</button>
                <button onClick={() => setActiveTab('commissions')} style={{ background: 'none', border: 'none', padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: activeTab === 'commissions' ? '700' : '500', color: activeTab === 'commissions' ? 'var(--etpos-text)' : 'var(--etpos-text-muted)', borderBottom: activeTab === 'commissions' ? '2px solid #6366f1' : '2px solid transparent', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>Commissions</button>
              </div>
            </div>
            <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
              <div className="table-controls-left">
                <div className="entries-dropdown">
                  <span>Show</span>
                  <select value={contactLoginEntries} onChange={e => setContactLoginEntries(e.target.value)}>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>
              </div>
              <div className="table-controls-right">
                <div className="tabs-search-wrapper">
                  <input type="text" value={contactLoginSearch} onChange={e => setContactLoginSearch(e.target.value)} placeholder="Search..." className="tabs-search-input" />
                  <button className="filter-toggle-btn" onClick={() => setShowContactLoginFilters(true)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg> Filters
                  </button>
                </div>
                <ExportDropdown show={showContactLoginExportDropdown} setShow={setShowContactLoginExportDropdown} />
                <button className="control-btn-primary" onClick={() => { setEditingContactLoginId(null); setContactLoginForm({ contact: '', email: '', mobile: '', alternate: '', familyContact: '', department: '', designation: '', commission: '', isActive: true, allowLogin: true }); fetchContactLoginResources(); setShowAddContactLoginModal(true); }}>
                  <span className="plus-icon">+</span> Add
                </button>
              </div>
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                    {['Action', 'Contact', 'Username', 'Name', 'Email', 'Department', 'Designation'].map((h, i) => (
                      <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {crmLoading ? (
                    <tr><td colSpan={7} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                  ) : filteredContactsLoginData.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                  ) : (
                    filteredContactsLoginData.map((c, i) => {
                      const rowId = c.id || c.user_id || (c.action && c.action.match(/\/contact-login\/(\d+)/)?.[1]) || `cl_${i}`;
                      return (
                        <tr key={rowId} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                          <td style={{ padding: '0.75rem 1rem', position: 'relative' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenContactLoginActionId(openContactLoginActionId === rowId ? null : rowId);
                              }}
                              style={{
                                background: '#38bdf8',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '0.25rem 0.75rem',
                                fontSize: '0.78rem',
                                fontWeight: '500',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                cursor: 'pointer',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                              }}
                            >
                              Action <span style={{ fontSize: '9px', marginLeft: '2px' }}>▼</span>
                            </button>
                            {openContactLoginActionId === rowId && (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  top: 'calc(100% - 4px)',
                                  left: '1rem',
                                  zIndex: 99,
                                  background: 'var(--etpos-card, #ffffff)',
                                  borderRadius: '6px',
                                  boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                                  border: '1px solid var(--etpos-border, #e2e8f0)',
                                  minWidth: '110px',
                                  padding: '0.35rem 0',
                                  display: 'flex',
                                  flexDirection: 'column'
                                }}
                              >
                                <button
                                  onClick={() => handleEditContactLogin(c)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '0.45rem 0.85rem',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '0.825rem',
                                    color: 'var(--etpos-text, #334155)',
                                    cursor: 'pointer'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--etpos-bg, #f1f5f9)'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                >
                                  <i className="fa fa-edit" style={{ color: 'var(--etpos-text-muted, #64748b)', fontSize: '0.85rem' }} /> Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteContactLogin(c)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: '0.45rem 0.85rem',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    fontSize: '0.825rem',
                                    color: 'var(--etpos-text, #334155)',
                                    cursor: 'pointer'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                >
                                  <i className="fa fa-trash" style={{ color: '#ef4444', fontSize: '0.85rem' }} /> Delete
                                </button>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.contact || c.contact_name || '-')}</td>
                          <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.username || '-')}</td>
                          <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.name || [c.surname, c.first_name, c.last_name].filter(Boolean).join(' ') || '-')}</td>
                          <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.email || '-')}</td>
                          <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.crm_department || c.department || '-')}</td>
                          <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.crm_designation || c.designation || '-')}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {filteredContactsLoginData.length > 0 ? 1 : 0} to {filteredContactsLoginData.length} of {filteredContactsLoginData.length} entries</span>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
                <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCommissionsTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem' }}>
          <div className="crm-sub-tabs" style={{ borderBottom: '1px solid var(--etpos-border)', marginBottom: '1.5rem', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
              <button onClick={() => setActiveTab('contacts_login')} style={{ background: 'none', border: 'none', padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: activeTab === 'contacts_login' ? '700' : '500', color: activeTab === 'contacts_login' ? 'var(--etpos-text)' : 'var(--etpos-text-muted)', borderBottom: activeTab === 'contacts_login' ? '2px solid #6366f1' : '2px solid transparent', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>Contacts Login</button>
              <button onClick={() => setActiveTab('commissions')} style={{ background: 'none', border: 'none', padding: '0.75rem 1.5rem', fontSize: '0.9rem', fontWeight: activeTab === 'commissions' ? '700' : '500', color: activeTab === 'commissions' ? 'var(--etpos-text)' : 'var(--etpos-text-muted)', borderBottom: activeTab === 'commissions' ? '2px solid #6366f1' : '2px solid transparent', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>Commissions</button>
            </div>
          </div>
          <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
            <div className="table-controls-left">
              <div className="entries-dropdown">
                <span>Show</span>
                <select value={commissionsEntries} onChange={e => setCommissionsEntries(e.target.value)}>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
            </div>
            <div className="table-controls-right">
              <div className="tabs-search-wrapper">
                <input type="text" value={commissionsSearch} onChange={e => setCommissionsSearch(e.target.value)} placeholder="Search..." className="tabs-search-input" />
                <button className="filter-toggle-btn" onClick={() => setShowCommissionsFilters(true)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg> Filters
                </button>
              </div>
              <ExportDropdown show={showCommissionsExportDropdown} setShow={setShowCommissionsExportDropdown} />
              {/* No Add Button for Commissions as requested */}
            </div>
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                  {['Date', 'Contact', 'Name', 'Mobile Number', 'Invoice No.', 'Location', 'Total commission'].map((h, i) => (
                    <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crmLoading ? (
                  <tr><td colSpan={7} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                ) : commissionsData.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                ) : (
                  commissionsData.map((c, i) => (
                    <tr key={c.id || i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.transaction_date || c.date || c.created_at)}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.contact_name || c.contact?.name || c.contact)}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.name || c.contact_person)}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.mobile || c.mobile_number)}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.invoice_no)}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.location_name || c.location)}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(c.total_commission || c.commission_amount || '0.00')}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--etpos-bg, #f8fafc)', fontWeight: '700', borderTop: '2px solid var(--etpos-border)' }}>
                  <td colSpan={6} style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700' }}>Total:</td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: '700' }}>
                    ${commissionsData.reduce((acc, c) => acc + (parseFloat(c.total_commission || c.commission_amount || 0) || 0), 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {commissionsData.length > 0 ? 1 : 0} to {commissionsData.length} of {commissionsData.length} entries</span>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCommissionsFiltersModal = () => (
    <div className="modal-overlay" onClick={() => setShowCommissionsFilters(false)}>
      <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%' }}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
          <button className="modal-close-btn" onClick={() => setShowCommissionsFilters(false)}>✕</button>
        </div>
        <div className="modal-content" style={{ padding: '1.5rem' }}>
          <div className="filter-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem' }}>
            <div className="filter-group">
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: 'var(--etpos-text-muted)', marginBottom: '0.4rem' }}>Contact:</label>
              <select style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.85rem' }}>
                <option>All</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <button style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1.25rem', background: 'var(--etpos-text)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '0.875rem' }}><RotateCcw size={14} /> Reset</button>
          <button className="modal-btn close-btn" onClick={() => setShowCommissionsFilters(false)} style={{ padding: '0.5rem 1.25rem', border: '1px solid var(--etpos-border)', background: 'transparent', borderRadius: '6px' }}>Close</button>
        </div>
      </div>
    </div>
  );

  const renderCellWithView = (val, title, assigned_to, status, contact_id, contact_name) => {
    let rawVal = '0';
    if (val !== null && val !== undefined) {
      if (typeof val === 'number') {
        rawVal = String(val);
      } else if (typeof val === 'string') {
        const match = val.match(/^(\d+)/);
        if (match) rawVal = match[1];
        else if (val.trim() && !val.includes('<a')) rawVal = val.trim();
      }
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <span>{rawVal}</span>
        <a
          href="#view"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleOpenReportFollowupsModal({ title, assigned_to, status, contact_id, contact_name });
          }}
          style={{
            color: '#0284c7',
            fontSize: '0.8rem',
            textDecoration: 'underline',
            cursor: 'pointer',
            marginTop: '2px'
          }}
        >
          View
        </a>
      </div>
    );
  };

  const renderReportsTab = () => {
    const filteredByUser = (reportsData.byUser || []).filter(r => {
      if (!reportsSearch.user) return true;
      const q = reportsSearch.user.toLowerCase();
      const name = (r.full_name || r.user || r.name || '').toLowerCase();
      return name.includes(q);
    });

    const filteredByContact = (reportsData.byContact || []).filter(r => {
      if (!reportsSearch.contact) return true;
      const q = reportsSearch.contact.toLowerCase();
      const name = (r.contact_name || r.name || r.supplier_business_name || r.contact || '').toLowerCase();
      return name.includes(q);
    });

    const filteredConversion = (reportsData.conversion || []).filter(r => {
      if (!reportsSearch.conversion) return true;
      const q = reportsSearch.conversion.toLowerCase();
      const name = (r.full_name || r.converted_by || r.user || r.name || '').toLowerCase();
      return name.includes(q);
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {/* Follow ups by user */}
        <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border)' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>Follow ups by user</h3>
          </div>
          <div className="crm-card-body" style={{ padding: '1.5rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.5rem' }}>Date Range:</label>
              <div style={{ maxWidth: '300px', width: '100%', padding: '0.5rem 0.75rem', background: 'var(--etpos-bg, #f1f5f9)', border: '1px solid var(--etpos-border)', borderRadius: '4px', fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>01/01/2026 - 12/31/2026</div>
            </div>

            <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
              <div className="table-controls-left">
                <div className="entries-dropdown">
                  <span>Show</span>
                  <select value={reportsEntries.user} onChange={e => setReportsEntries({ ...reportsEntries, user: e.target.value })}>
                    <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>
              </div>
              <div className="table-controls-right">
                <ExportDropdown show={showReportsExport.user} setShow={(val) => setShowReportsExport({ ...showReportsExport, user: val })} />
                <div className="tabs-search-wrapper">
                  <input type="text" value={reportsSearch.user} onChange={e => setReportsSearch({ ...reportsSearch, user: e.target.value })} placeholder="Search..." className="tabs-search-input" />
                </div>
              </div>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                    {['User', 'Scheduled', 'Open', 'Cancelled', 'Completed', 'Others', 'Total follow ups'].map((h, i) => (
                      <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {crmLoading ? (
                    <tr><td colSpan={7} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                  ) : filteredByUser.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                  ) : (
                    filteredByUser.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(r.full_name || r.user || r.name || '-')}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.count_scheduled, `Follow Ups for ${r.full_name || 'User'} (Scheduled)`, r.user_id, 'scheduled', null)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.count_open, `Follow Ups for ${r.full_name || 'User'} (Open)`, r.user_id, 'open', null)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.count_cancelled, `Follow Ups for ${r.full_name || 'User'} (Cancelled)`, r.user_id, 'cancelled', null)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.count_completed, `Follow Ups for ${r.full_name || 'User'} (Completed)`, r.user_id, 'completed', null)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.count_nulled, `Follow Ups for ${r.full_name || 'User'} (Others)`, r.user_id, 'none', null)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.total_follow_ups, `Follow Ups for ${r.full_name || 'User'} (Total)`, r.user_id, 'all', null)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {filteredByUser.length > 0 ? 1 : 0} to {filteredByUser.length} of {filteredByUser.length} entries</span>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
                <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
              </div>
            </div>
          </div>
        </div>

        {/* Follow ups by contacts */}
        <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border)' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>Follow ups by contacts</h3>
          </div>
          <div className="crm-card-body" style={{ padding: '1.5rem' }}>
            <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
              <div className="table-controls-left">
                <div className="entries-dropdown">
                  <span>Show</span>
                  <select value={reportsEntries.contact} onChange={e => setReportsEntries({ ...reportsEntries, contact: e.target.value })}>
                    <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>
              </div>
              <div className="table-controls-right">
                <ExportDropdown show={showReportsExport.contact} setShow={(val) => setShowReportsExport({ ...showReportsExport, contact: val })} />
                <div className="tabs-search-wrapper">
                  <input type="text" value={reportsSearch.contact} onChange={e => setReportsSearch({ ...reportsSearch, contact: e.target.value })} placeholder="Search..." className="tabs-search-input" />
                </div>
              </div>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                    {['Contact', 'Scheduled', 'Open', 'Cancelled', 'Completed', 'Others', 'Total follow ups'].map((h, i) => (
                      <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {crmLoading ? (
                    <tr><td colSpan={7} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                  ) : filteredByContact.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                  ) : (
                    filteredByContact.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(r.contact_name || r.name || r.contact || '-')}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.count_scheduled, `Follow Ups for ${r.contact_name || r.name || 'Contact'} (Scheduled)`, null, 'scheduled', r.id || r.contact_id, r.contact_name || r.name)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.count_open, `Follow Ups for ${r.contact_name || r.name || 'Contact'} (Open)`, null, 'open', r.id || r.contact_id, r.contact_name || r.name)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.count_cancelled, `Follow Ups for ${r.contact_name || r.name || 'Contact'} (Cancelled)`, null, 'cancelled', r.id || r.contact_id, r.contact_name || r.name)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.count_completed, `Follow Ups for ${r.contact_name || r.name || 'Contact'} (Completed)`, null, 'completed', r.id || r.contact_id, r.contact_name || r.name)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.count_nulled, `Follow Ups for ${r.contact_name || r.name || 'Contact'} (Others)`, null, 'none', r.id || r.contact_id, r.contact_name || r.name)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCellWithView(r.total_follow_ups, `Follow Ups for ${r.contact_name || r.name || 'Contact'} (Total)`, null, 'all', r.id || r.contact_id, r.contact_name || r.name)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {filteredByContact.length > 0 ? 1 : 0} to {filteredByContact.length} of {filteredByContact.length} entries</span>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
                <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
              </div>
            </div>
          </div>
        </div>

        {/* Leads to customer conversion */}
        <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border)' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>Leads to customer conversion</h3>
          </div>
          <div className="crm-card-body" style={{ padding: '1.5rem' }}>
            <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
              <div className="table-controls-left">
                <div className="entries-dropdown">
                  <span>Show</span>
                  <select value={reportsEntries.conversion} onChange={e => setReportsEntries({ ...reportsEntries, conversion: e.target.value })}>
                    <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>
              </div>
              <div className="table-controls-right">
                <ExportDropdown show={showReportsExport.conversion} setShow={(val) => setShowReportsExport({ ...showReportsExport, conversion: val })} />
                <div className="tabs-search-wrapper">
                  <input type="text" value={reportsSearch.conversion} onChange={e => setReportsSearch({ ...reportsSearch, conversion: e.target.value })} placeholder="Search..." className="tabs-search-input" />
                </div>
              </div>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                    {['Converted By', 'Total'].map((h, i) => (
                      <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {crmLoading ? (
                    <tr><td colSpan={2} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                  ) : filteredConversion.length === 0 ? (
                    <tr><td colSpan={2} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                  ) : (
                    filteredConversion.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(r.full_name || r.converted_by || r.user || r.name || '-')}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{renderCleanHtml(r.total_conversions !== undefined ? r.total_conversions : (r.total || '0'))}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {filteredConversion.length > 0 ? 1 : 0} to {filteredConversion.length} of {filteredConversion.length} entries</span>
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
                <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReportFollowupsModal = () => {
    if (!showReportFollowupsModal) return null;
    return (
      <div className="modal-overlay" onClick={() => setShowReportFollowupsModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
        <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: '950px', width: '92%', borderRadius: '12px', background: 'var(--etpos-card, #ffffff)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid var(--etpos-border, #e2e8f0)', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
          <div style={{ padding: '1.25rem 1.75rem', background: 'var(--etpos-bg, #f8fafc)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: 'var(--etpos-text, #0f172a)' }}>{reportFollowupsFilter.title || 'Follow-Ups List'}</h3>
            <button onClick={() => setShowReportFollowupsModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: 'var(--etpos-text-muted, #64748b)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
          </div>
          <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1, width: '100%', boxSizing: 'border-box' }}>
            {reportFollowupsLoading ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)', fontSize: '0.95rem' }}>Loading follow ups...</div>
            ) : reportFollowupsList.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)', fontSize: '0.95rem' }}>No follow ups found for this selection.</div>
            ) : (
              <div style={{ width: '100%', overflowX: 'auto', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: 'var(--etpos-bg, #f1f5f9)', borderBottom: '2px solid var(--etpos-border, #e2e8f0)' }}>
                      {['Contact', 'Status', 'Schedule Type', 'Start DateTime', 'End DateTime', 'Added By'].map((h, i) => (
                        <th key={i} style={{ padding: '0.85rem 1.15rem', fontWeight: '700', color: 'var(--etpos-text, #334155)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportFollowupsList.map((f, i) => (
                      <tr key={f.id || i} style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)' }}>
                        <td style={{ padding: '0.85rem 1.15rem', whiteSpace: 'nowrap' }}>{renderCleanHtml(f.contact || f.biz_name || '-')}</td>
                        <td style={{ padding: '0.85rem 1.15rem', whiteSpace: 'nowrap' }}>{renderCleanHtml(f.status || 'None')}</td>
                        <td style={{ padding: '0.85rem 1.15rem', whiteSpace: 'nowrap' }}>{renderCleanHtml(f.schedule_type || '-')}</td>
                        <td style={{ padding: '0.85rem 1.15rem', whiteSpace: 'nowrap' }}>{renderCleanHtml(f.start_datetime || '-')}</td>
                        <td style={{ padding: '0.85rem 1.15rem', whiteSpace: 'nowrap' }}>{renderCleanHtml(f.end_datetime || '-')}</td>
                        <td style={{ padding: '0.85rem 1.15rem', whiteSpace: 'nowrap' }}>{renderCleanHtml(f.added_by || (f.surname ? `${f.surname} ${f.first_name || ''}` : '-'))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div style={{ padding: '1rem 1.75rem', background: 'var(--etpos-bg, #f8fafc)', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowReportFollowupsModal(false)} style={{ background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '0.6rem 2.25rem', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)' }}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const renderProposalTemplateTab = () => {
    const template = Array.isArray(proposalTemplatesData) ? proposalTemplatesData[0] : proposalTemplatesData;
    const hasTemplate = Boolean(template && (template.subject || template.title || template.id));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', minHeight: '380px', padding: '2.5rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {crmLoading ? (
            <div style={{ textAlign: 'center', color: 'var(--etpos-text-muted)', padding: '3rem' }}>Loading proposal template...</div>
          ) : hasTemplate ? (
            <div style={{ width: '100%', maxWidth: '480px', border: '2px solid #38bdf8', borderRadius: '10px', background: 'var(--etpos-card, #ffffff)', overflow: 'hidden', boxShadow: '0 4px 14px rgba(56, 189, 248, 0.12)' }}>
              <div style={{ padding: '1.75rem 2rem', textAlign: 'center', background: 'var(--etpos-card, #ffffff)' }}>
                <strong style={{ fontSize: '1.15rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', wordBreak: 'break-word' }}>
                  {template.subject || template.title || template.name || 'Proposal Template'}
                </strong>
              </div>
              <div style={{ padding: '1rem 1.5rem', background: 'var(--etpos-bg, #f8fafc)', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-around', gap: '0.75rem' }}>
                <button
                  onClick={() => {
                    setProposalTemplateForm({
                      title: template.name || template.title || '',
                      subject: template.subject || '',
                      body: template.body || ''
                    });
                    setShowAddProposalTemplateModal(true);
                  }}
                  style={{ background: '#7c3aed', color: '#ffffff', border: 'none', borderRadius: '20px', padding: '0.45rem 1.5rem', fontWeight: '700', fontSize: '0.875rem', cursor: 'pointer', boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)' }}
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    setProposalTemplateForm({
                      title: template.name || template.title || '',
                      subject: template.subject || '',
                      body: template.body || ''
                    });
                    setShowAddProposalTemplateModal(true);
                  }}
                  style={{ background: '#0ea5e9', color: '#ffffff', border: 'none', borderRadius: '20px', padding: '0.45rem 1.4rem', fontWeight: '700', fontSize: '0.875rem', cursor: 'pointer', boxShadow: '0 2px 4px rgba(14, 165, 233, 0.2)' }}
                >
                  View
                </button>
                <button
                  onClick={() => setShowSendProposalModal(true)}
                  style={{ background: '#10b981', color: '#ffffff', border: 'none', borderRadius: '20px', padding: '0.45rem 1.5rem', fontWeight: '700', fontSize: '0.875rem', cursor: 'pointer', boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)' }}
                >
                  Send
                </button>
              </div>
            </div>
          ) : (
            <div style={{ width: '100%', maxWidth: '500px', padding: '2rem', background: 'var(--etpos-bg, #eff6ff)', border: '1px solid var(--etpos-border, #bfdbfe)', borderRadius: '10px', textAlign: 'center' }}>
              <h4 style={{ margin: '0 0 1rem 0', color: 'var(--etpos-text, #1e40af)', fontSize: '1.1rem', fontWeight: '700' }}>No template found</h4>
              <p style={{ margin: '0 0 1.5rem 0', color: 'var(--etpos-text-muted, #1e3a8a)', fontSize: '0.9rem' }}>You haven't created a proposal template yet.</p>
              <button
                onClick={() => setShowAddProposalTemplateModal(true)}
                style={{ background: 'linear-gradient(to right, #4f46e5, #3b82f6)', color: '#ffffff', border: 'none', borderRadius: '20px', padding: '0.65rem 2rem', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer', boxShadow: '0 4px 10px rgba(79, 70, 229, 0.25)' }}
              >
                + Add Template
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAddProposalTemplateModal = () => (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', width: '850px', maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: 'var(--etpos-text)' }}>Add Proposal Template</h3>
          <button onClick={() => setShowAddProposalTemplateModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: 'var(--etpos-text-muted)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>
        <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Template Name:*</label>
            <input type="text" placeholder="Template Name" value={proposalTemplateForm.title} onChange={e => setProposalTemplateForm({ ...proposalTemplateForm, title: e.target.value })} style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.95rem' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Email Subject:*</label>
            <input type="text" placeholder="Subject" value={proposalTemplateForm.subject} onChange={e => setProposalTemplateForm({ ...proposalTemplateForm, subject: e.target.value })} style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.95rem' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Template Body:*</label>
            <div style={{ border: '1px solid var(--etpos-border)', borderRadius: '6px', overflow: 'hidden' }}>
              <Editor
                init={{
                  height: 350,
                  menubar: false,
                  plugins: [
                    'advlist autolink lists link image charmap print preview anchor',
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime media table paste code help wordcount'
                  ],
                  toolbar: 'undo redo | formatselect | ' +
                    'bold italic backcolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
                value={proposalTemplateForm.body}
                onEditorChange={(content) => setProposalTemplateForm({ ...proposalTemplateForm, body: content })}
              />
            </div>
            <div style={{ marginTop: '1rem' }}>
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600' }}>Available Tags:</p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {['{contact_name}', '{business_name}', '{proposal_date}', '{total_amount}', '{items_table}'].map(tag => (
                  <button key={tag} onClick={() => setProposalTemplateForm({ ...proposalTemplateForm, body: proposalTemplateForm.body + tag })} style={{ padding: '0.35rem 0.75rem', background: 'var(--etpos-bg, #f1f5f9)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '4px', fontSize: '0.75rem', color: '#4f46e5', cursor: 'pointer', fontWeight: '600' }}>{tag}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: '1.25rem 2rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
          <button style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.75rem 3rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Save</button>
          <button onClick={() => setShowAddProposalTemplateModal(false)} style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.75rem 3rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );

  const renderProposalsTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem' }}>
          <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
            <div className="table-controls-left">
              <div className="entries-dropdown">
                <span>Show</span>
                <select value={proposalsEntries} onChange={e => setProposalsEntries(e.target.value)}>
                  <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
            </div>
            <div className="table-controls-right">
              <div className="tabs-search-wrapper">
                <input type="text" value={proposalsSearch} onChange={e => setProposalsSearch(e.target.value)} placeholder="Search..." className="tabs-search-input" />
              </div>
              <button className="control-btn-primary" onClick={() => setShowSendProposalModal(true)}>
                <span className="plus-icon">+</span> Send Proposal
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                  {['Action', 'Subject', 'Recipient', 'Status', 'Total Amount', 'Sent At'].map((h, i) => (
                    <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crmLoading ? (
                  <tr><td colSpan={6} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                ) : proposalsData.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No proposals found</td></tr>
                ) : (
                  proposalsData.map((p, i) => (
                    <tr key={p.id || i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>-</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{p.subject || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{p.recipient?.name || p.recipient || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{p.status || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{p.total_amount || '0'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{p.sent_at || p.created_at || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {proposalsData.length > 0 ? 1 : 0} to {proposalsData.length} of {proposalsData.length} entries</span>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const handleSendProposal = async () => {
    if (!proposalForm.contact_id) {
      toast.warning('Please select a recipient (Send to).');
      return;
    }
    if (!proposalForm.subject.trim()) {
      toast.warning('Subject is required.');
      return;
    }
    if (!proposalForm.body.trim()) {
      toast.warning('Email body is required.');
      return;
    }
    setCrmLoading(true);
    try {
      await apiService.sendCrmProposal({
        contact_id: proposalForm.contact_id,
        cc: proposalForm.cc,
        bcc: proposalForm.bcc,
        subject: proposalForm.subject,
        body: proposalForm.body
      });
      toast.success('Proposal sent successfully!');
      setShowSendProposalModal(false);
      setProposalForm({ contact_id: '', cc: '', bcc: '', subject: '', body: '' });
      fetchProposals();
    } catch (e) {
      /* console.error('Failed to send proposal', e); */
      toast.error('Failed to send proposal. Please try again.');
    } finally {
      setCrmLoading(false);
    }
  };

  const renderSendProposalModal = () => {
    if (!showSendProposalModal) return null;
    const customerList = Array.isArray(followUpResources?.customers)
      ? followUpResources.customers
      : (followUpResources?.customers && typeof followUpResources.customers === 'object'
        ? Object.entries(followUpResources.customers).map(([id, name]) => ({ id, name }))
        : []);
    const leadList = Array.isArray(leadsData) ? leadsData : [];
    const contactLoginList = Array.isArray(contactsLoginData) ? contactsLoginData : [];

    const contactOptions = [...customerList, ...leadList, ...contactLoginList];
    const uniqueContacts = Array.from(new Map(contactOptions.map(c => [String(c.id || c.user_id || c.contact_id), c])).values());

    return (
      <div className="modal-overlay" onClick={() => setShowSendProposalModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
        <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--etpos-card, #ffffff)', borderRadius: '12px', width: '880px', maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '1.25rem 1.75rem', background: 'var(--etpos-bg, #f8fafc)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: 'var(--etpos-text, #0f172a)' }}>Proposal <span style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--etpos-text-muted, #64748b)' }}>Send</span></h3>
            </div>
            <button onClick={() => setShowSendProposalModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: 'var(--etpos-text-muted, #64748b)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
          </div>
          <div style={{ padding: '1.75rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Send to:*</label>
              <select value={proposalForm.contact_id} onChange={e => setProposalForm({ ...proposalForm, contact_id: e.target.value })} style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #0f172a)' }}>
                <option value="">Please Select</option>
                {uniqueContacts.map((c, i) => {
                  const id = c.id || c.user_id || c.contact_id;
                  const name = c.name || c.supplier_business_name || [c.surname, c.first_name, c.last_name].filter(Boolean).join(' ') || c.contact || `Contact #${id}`;
                  return <option key={id || i} value={id}>{name}</option>;
                })}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>CC:</label>
              <input type="text" value={proposalForm.cc} onChange={e => setProposalForm({ ...proposalForm, cc: e.target.value })} placeholder="CC" style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #0f172a)' }} />
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.78rem', color: 'var(--etpos-text-muted, #64748b)' }}>Comma separated values of emails</p>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>BCC:</label>
              <input type="text" value={proposalForm.bcc} onChange={e => setProposalForm({ ...proposalForm, bcc: e.target.value })} placeholder="BCC" style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #0f172a)' }} />
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.78rem', color: 'var(--etpos-text-muted, #64748b)' }}>Comma separated values of emails</p>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Subject:*</label>
              <input type="text" value={proposalForm.subject} onChange={e => setProposalForm({ ...proposalForm, subject: e.target.value })} placeholder="Subject" style={{ width: '100%', padding: '0.65rem 0.85rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #0f172a)' }} required />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text, #1e293b)', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Email Body:*</label>
              <textarea rows={8} value={proposalForm.body} onChange={e => setProposalForm({ ...proposalForm, body: e.target.value })} placeholder="Email Body..." style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '6px', fontSize: '0.9rem', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #0f172a)', minHeight: '180px', resize: 'vertical' }} required />
            </div>
          </div>
          <div style={{ padding: '1.25rem 2rem', background: 'var(--etpos-bg, #f8fafc)', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button onClick={() => setShowSendProposalModal(false)} style={{ background: '#64748b', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.6rem 1.75rem', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer' }}>Cancel</button>
            <button onClick={handleSendProposal} disabled={crmLoading} style={{ background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.6rem 2.25rem', fontWeight: '700', fontSize: '0.875rem', cursor: 'pointer', boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)' }}>{crmLoading ? 'Sending...' : 'Send'}</button>
          </div>
        </div>
      </div>
    );
  };

  const renderSourcesTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem' }}>
          <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
            <div className="table-controls-left">
              <div className="entries-dropdown">
                <span>Show</span>
                <select value={sourcesEntries} onChange={e => setSourcesEntries(e.target.value)}>
                  <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
            </div>
            <div className="table-controls-right">
              <div className="tabs-search-wrapper">
                <input type="text" value={sourcesSearch} onChange={e => setSourcesSearch(e.target.value)} placeholder="Search..." className="tabs-search-input" />
              </div>
              <button className="control-btn-primary" onClick={() => setShowAddSourceModal(true)}>
                <span className="plus-icon">+</span> Add
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                  {['Source', 'Description', 'Action'].map((h, i) => (
                    <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crmLoading ? (
                  <tr><td colSpan={3} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                ) : sourcesData.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                ) : (
                  sourcesData.map((s, i) => (
                    <tr key={s.id || i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>{s.name || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{s.description || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                          <button onClick={() => handleEditCategory(s.id, s.name, s.description, fetchSources)} title="Edit" style={{ padding: '0.25rem 0.65rem', background: 'var(--etpos-card, #fff)', color: '#6366f1', border: '1px solid #6366f1', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>✏️ Edit</button>
                          <button onClick={() => handleDeleteCategory(s.id, fetchSources)} title="Delete" style={{ padding: '0.25rem 0.65rem', background: 'var(--etpos-card, #fff)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>🗑️ Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {sourcesData.length > 0 ? 1 : 0} to {sourcesData.length} of {sourcesData.length} entries</span>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAddSourceModal = () => (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', width: '500px', maxWidth: '96vw', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text)' }}>Add</h3>
          <button onClick={() => setShowAddSourceModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: 'var(--etpos-text-muted)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>
        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Source:*</label>
            <input type="text" placeholder="Source" value={sourceForm.name} onChange={e => setSourceForm({ ...sourceForm, name: e.target.value })} style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.9rem' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Description:</label>
            <textarea placeholder="Description" value={sourceForm.description} onChange={e => setSourceForm({ ...sourceForm, description: e.target.value })} style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.9rem', minHeight: '100px', resize: 'vertical' }} />
          </div>
        </div>
        <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
          <button onClick={handleSaveSource} style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2.5rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Save</button>
          <button onClick={() => setShowAddSourceModal(false)} style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2.5rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );

  const renderLifeStageTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem' }}>
          <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
            <div className="table-controls-left">
              <div className="entries-dropdown">
                <span>Show</span>
                <select value={lifeStageEntries} onChange={e => setLifeStageEntries(e.target.value)}>
                  <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
            </div>
            <div className="table-controls-right">
              <div className="tabs-search-wrapper">
                <input type="text" value={lifeStageSearch} onChange={e => setLifeStageSearch(e.target.value)} placeholder="Search..." className="tabs-search-input" />
              </div>
              <button className="control-btn-primary" onClick={() => setShowAddLifeStageModal(true)}>
                <span className="plus-icon">+</span> Add
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                  {['Life Stage', 'Description', 'Action'].map((h, i) => (
                    <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crmLoading ? (
                  <tr><td colSpan={3} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                ) : lifeStagesData.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                ) : (
                  lifeStagesData.map((s, i) => (
                    <tr key={s.id || i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>{s.name || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{s.description || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                          <button onClick={() => handleEditCategory(s.id, s.name, s.description, fetchLifeStages)} title="Edit" style={{ padding: '0.25rem 0.65rem', background: 'var(--etpos-card, #fff)', color: '#6366f1', border: '1px solid #6366f1', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>✏️ Edit</button>
                          <button onClick={() => handleDeleteCategory(s.id, fetchLifeStages)} title="Delete" style={{ padding: '0.25rem 0.65rem', background: 'var(--etpos-card, #fff)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>🗑️ Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {lifeStagesData.length > 0 ? 1 : 0} to {lifeStagesData.length} of {lifeStagesData.length} entries</span>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAddLifeStageModal = () => (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', width: '500px', maxWidth: '96vw', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text)' }}>Add</h3>
          <button onClick={() => setShowAddLifeStageModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: 'var(--etpos-text-muted)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>
        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Life Stage:*</label>
            <input type="text" placeholder="Life Stage" value={lifeStageForm.name} onChange={e => setLifeStageForm({ ...lifeStageForm, name: e.target.value })} style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.9rem' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Description:</label>
            <textarea placeholder="Description" value={lifeStageForm.description} onChange={e => setLifeStageForm({ ...lifeStageForm, description: e.target.value })} style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.9rem', minHeight: '100px', resize: 'vertical' }} />
          </div>
        </div>
        <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
          <button onClick={handleSaveLifeStage} style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2.5rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Save</button>
          <button onClick={() => setShowAddLifeStageModal(false)} style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2.5rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );

  const renderFollowupCategoryTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem' }}>
          <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
            <div className="table-controls-left">
              <div className="entries-dropdown">
                <span>Show</span>
                <select value={followupCategoryEntries} onChange={e => setFollowupCategoryEntries(e.target.value)}>
                  <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
            </div>
            <div className="table-controls-right">
              <div className="tabs-search-wrapper">
                <input type="text" value={followupCategorySearch} onChange={e => setFollowupCategorySearch(e.target.value)} placeholder="Search..." className="tabs-search-input" />
              </div>
              <button className="control-btn-primary" onClick={() => setShowAddFollowupCategoryModal(true)}>
                <span className="plus-icon">+</span> Add
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                  {['Followup Category', 'Description', 'Action'].map((h, i) => (
                    <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crmLoading ? (
                  <tr><td colSpan={3} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                ) : followupCategoriesData.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                ) : (
                  followupCategoriesData.map((s, i) => (
                    <tr key={s.id || i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>{s.name || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{s.description || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.35rem' }}>
                          <button onClick={() => handleEditCategory(s.id, s.name, s.description, fetchFollowupCategories)} title="Edit" style={{ padding: '0.25rem 0.65rem', background: 'var(--etpos-card, #fff)', color: '#6366f1', border: '1px solid #6366f1', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>✏️ Edit</button>
                          <button onClick={() => handleDeleteCategory(s.id, fetchFollowupCategories)} title="Delete" style={{ padding: '0.25rem 0.65rem', background: 'var(--etpos-card, #fff)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>🗑️ Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {followupCategoriesData.length > 0 ? 1 : 0} to {followupCategoriesData.length} of {followupCategoriesData.length} entries</span>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAddFollowupCategoryModal = () => (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', width: '500px', maxWidth: '96vw', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text)' }}>Add</h3>
          <button onClick={() => setShowAddFollowupCategoryModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: 'var(--etpos-text-muted)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>
        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Followup Category:*</label>
            <input type="text" placeholder="Followup Category" value={followupCategoryForm.name} onChange={e => setFollowupCategoryForm({ ...followupCategoryForm, name: e.target.value })} style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.9rem' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: 'var(--etpos-text)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Description:</label>
            <textarea placeholder="Description" value={followupCategoryForm.description} onChange={e => setFollowupCategoryForm({ ...followupCategoryForm, description: e.target.value })} style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.9rem', minHeight: '100px', resize: 'vertical' }} />
          </div>
        </div>
        <div style={{ padding: '1.25rem 1.5rem', borderTop: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
          <button onClick={handleSaveFollowupCategory} style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2.5rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Save</button>
          <button onClick={() => setShowAddFollowupCategoryModal(false)} style={{ background: '#334155', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.6rem 2.5rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
        {/* Order Request Settings */}
        <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', padding: '1.5rem' }}>
          <h4 style={{ margin: '0 0 1.25rem 0', display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--etpos-text)', fontSize: '1rem', fontWeight: '700' }}>
            <span style={{ background: '#eef2ff', padding: '0.4rem', borderRadius: '6px', color: '#4f46e5' }}><Users size={18} /></span> Order Request
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, fontWeight: '600', fontSize: '0.9rem' }}>Enable Order Request</p>
                <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--etpos-text-muted)' }}>Allow contacts to place order requests via portal</p>
              </div>
              <input type="checkbox" checked={crmSettingsData.enable_order_request} onChange={e => setCrmSettingsData({ ...crmSettingsData, enable_order_request: e.target.checked })} style={{ width: '1.2rem', height: '1.2rem', cursor: 'pointer' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Order Request Prefix:</label>
              <input type="text" value={crmSettingsData.order_request_prefix} onChange={e => setCrmSettingsData({ ...crmSettingsData, order_request_prefix: e.target.value })} style={{ width: '100%', padding: '0.65rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
        <button onClick={handleSaveCrmSettings} style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.75rem 4rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)' }}>Save Settings</button>
      </div>
    </div>
  );



  const renderTabs = () => (
    <div className="tabs-container">
      <div className="tabs-nav">
        {[
          { key: 'crm', label: 'CRM' },
          { key: 'leads', label: 'Leads' },
          { key: 'followups', label: 'Follow ups' },
          { key: 'campaigns', label: 'Campaigns' },
          { key: 'contacts_login', label: 'Contacts Login' },
          { key: 'reports', label: 'Reports' },
          { key: 'proposal_template', label: 'Proposal template' },
          { key: 'proposals', label: 'Proposals' },
          { key: 'call_logs', label: 'Call Log' },
          { key: 'sources', label: 'Sources' },
          { key: 'life_stage', label: 'Life Stage' },
          { key: 'followup_category', label: 'Followup Category' },
          { key: 'settings', label: 'Settings' },
        ].map(tab => (
          <button
            key={tab.key}
            className={`tab-button ${(activeTab === tab.key || (tab.key === 'contacts_login' && activeTab === 'commissions')) ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderCallLogsTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem' }}>
          <div className="table-controls" style={{ padding: '0 0 1.25rem 0' }}>
            <div className="table-controls-left">
              <div className="entries-dropdown">
                <span>Show</span>
                <select><option>25</option><option>50</option><option>100</option></select>
                <span>entries</span>
              </div>
            </div>
            <div className="table-controls-right">
              <div className="tabs-search-wrapper">
                <input type="text" placeholder="Search..." className="tabs-search-input" />
              </div>
            </div>
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                  {['Date', 'Contact', 'Call Type', 'Duration', 'Notes'].map((h, i) => (
                    <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crmLoading ? (
                  <tr><td colSpan={5} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                ) : callLogsData.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                ) : (
                  callLogsData.map((log, i) => (
                    <tr key={log.id || i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>{log.date || log.created_at || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{log.contact?.name || log.contact || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{log.call_type || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{log.duration || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{log.notes || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>Showing {callLogsData.length > 0 ? 1 : 0} to {callLogsData.length} of {callLogsData.length} entries</span>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Previous</button>
              <button style={{ padding: '0.45rem 0.85rem', border: '1px solid var(--etpos-border)', background: 'var(--etpos-card, #fff)', borderRadius: '4px', cursor: 'not-allowed' }}>Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrderRequestsTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div className="crm-card-body" style={{ padding: '1.25rem 1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h4 style={{ margin: 0, fontWeight: '700', fontSize: '1.1rem' }}>All Order Requests</h4>
          </div>
          <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                  {['#', 'Date', 'Invoice No', 'Contact Name', 'Location', 'Status', 'Total Items', 'Total Amount'].map((h, i) => (
                    <th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {crmLoading ? (
                  <tr><td colSpan={8} style={{ padding: '3.5rem', textAlign: 'center' }}>Loading...</td></tr>
                ) : orderRequestsData.length === 0 ? (
                  <tr><td colSpan={8} style={{ padding: '3.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No order requests found</td></tr>
                ) : (
                  orderRequestsData.map((ord, i) => (
                    <tr key={ord.id || i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>{i + 1}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{ord.transaction_date || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{ord.invoice_no || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{ord.contact_name || ord.contact?.name || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{ord.location_name || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{ padding: '0.2rem 0.6rem', borderRadius: '4px', background: ord.status === 'completed' ? '#dcfce7' : '#fef3c7', color: ord.status === 'completed' ? '#166534' : '#92400e', fontSize: '0.75rem', fontWeight: '600', textTransform: 'capitalize' }}>
                          {ord.status || 'ordered'}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>{ord.total_items || 1}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{ord.final_total || 0}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMarketplaceTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', border: '1px solid var(--etpos-border)', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <h4 style={{ margin: '0 0 0.75rem', fontWeight: '700', fontSize: '1.1rem' }}>B2B Marketplace Settings</h4>
        <p style={{ color: 'var(--etpos-text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
          Configure B2B lead generation marketplace integration parameters and lead import preferences.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem', maxWidth: '650px' }}>
          <div>
            <label style={{ display: 'block', fontWeight: '700', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Marketplace API Key:</label>
            <input type="text" placeholder="Enter API Key" style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: '700', marginBottom: '0.4rem', fontSize: '0.875rem' }}>Default Lead Source:</label>
            <select style={{ width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }}>
              <option value="">Marketplace</option>
            </select>
          </div>
        </div>
        <div style={{ marginTop: '1.5rem' }}>
          <button onClick={() => toast.success('B2B Marketplace settings updated successfully!')} style={{ padding: '0.6rem 1.75rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer' }}>Save Settings</button>
        </div>
      </div>
    </div>
  );

  const renderScheduleLogsModal = () => (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--etpos-card)', borderRadius: '10px', width: '600px', maxWidth: '96vw', maxHeight: '85vh', overflowY: 'auto', padding: '1.5rem', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: 'var(--etpos-text)' }}>Follow-up Activity Logs</h3>
          <button onClick={() => setShowScheduleLogsModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: 'var(--etpos-text-muted)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'block', fontWeight: '700', fontSize: '0.875rem', marginBottom: '0.4rem', color: 'var(--etpos-text)' }}>Add Log Entry:</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input type="text" value={newLogDescription} onChange={e => setNewLogDescription(e.target.value)} placeholder="Type log note..." style={{ flex: 1, padding: '0.55rem 0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }} />
            <button onClick={handleAddScheduleLog} style={{ padding: '0.55rem 1.25rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer', fontSize: '0.875rem' }}>Add Log</button>
          </div>
        </div>
        <div style={{ border: '1px solid var(--etpos-border)', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: 'var(--etpos-bg)', borderBottom: '1px solid var(--etpos-border)' }}>
                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: '700' }}>Time</th>
                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: '700' }}>Logged By</th>
                <th style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: '700' }}>Description</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(activeScheduleLogs) && activeScheduleLogs.length > 0 ? (
                activeScheduleLogs.map((l, i) => (
                  <tr key={l.id || i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                    <td style={{ padding: '0.6rem 0.75rem' }}>{l.created_at || '-'}</td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>{l.createdBy?.user_full_name || l.created_by_name || '-'}</td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>{l.description || l.subject || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={3} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No log history found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderFollowUpLogModal = () => {
    if (!showFollowUpLogModal) return null;

    const logsList = Array.isArray(followUpLogData)
      ? followUpLogData
      : (Array.isArray(followUpLogData?.logs)
        ? followUpLogData.logs
        : (Array.isArray(followUpLogData?.data)
          ? followUpLogData.data
          : (Array.isArray(followUpLogData?.schedule_logs)
            ? followUpLogData.schedule_logs
            : [])));

    const htmlLog = typeof followUpLogData === 'string'
      ? followUpLogData
      : (typeof followUpLogData?.log === 'string' ? followUpLogData.log : null);

    return (
      <div className="modal-overlay" onClick={() => setShowFollowUpLogModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
        <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--etpos-card, #ffffff)', borderRadius: '12px', width: '700px', maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '1.25rem 1.75rem', background: 'var(--etpos-bg, #f8fafc)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: 'var(--etpos-text, #0f172a)' }}>Follow Up Logs</h3>
            <button onClick={() => setShowFollowUpLogModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: 'var(--etpos-text-muted, #64748b)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
          </div>
          <div style={{ padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {htmlLog ? (
              <div style={{ padding: '0.5rem', fontSize: '0.9rem' }}>
                {renderCleanHtml(htmlLog)}
              </div>
            ) : (
              <div style={{ border: '1px solid var(--etpos-border)', borderRadius: '8px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--etpos-bg)', borderBottom: '1px solid var(--etpos-border)' }}>
                      <th style={{ padding: '0.65rem 0.85rem', textAlign: 'left', fontWeight: '700' }}>Date</th>
                      <th style={{ padding: '0.65rem 0.85rem', textAlign: 'left', fontWeight: '700' }}>Description</th>
                      <th style={{ padding: '0.65rem 0.85rem', textAlign: 'left', fontWeight: '700' }}>Added By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsList.length === 0 ? (
                      <tr><td colSpan={3} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No follow up log entries found</td></tr>
                    ) : (
                      logsList.map((log, idx) => (
                        <tr key={log.id || idx} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                          <td style={{ padding: '0.65rem 0.85rem', whiteSpace: 'nowrap' }}>{renderCleanHtml(log.created_at || log.start_datetime || log.date || '-')}</td>
                          <td style={{ padding: '0.65rem 0.85rem' }}>{renderCleanHtml(log.description || log.subject || log.note || '-')}</td>
                          <td style={{ padding: '0.65rem 0.85rem' }}>{renderCleanHtml(log.created_by_name || log.createdBy?.user_full_name || log.user || '-')}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div style={{ padding: '1rem 1.75rem', background: 'var(--etpos-bg, #f8fafc)', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowFollowUpLogModal(false)} style={{ background: '#64748b', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.55rem 1.75rem', fontWeight: '600', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const renderAddFollowUpLogModal = () => {
    if (!showAddFollowUpLogModal) return null;
    return (
      <div className="modal-overlay" onClick={() => setShowAddFollowUpLogModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
        <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--etpos-card, #ffffff)', borderRadius: '12px', width: '600px', maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '1.25rem 1.75rem', background: 'var(--etpos-bg, #f8fafc)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: 'var(--etpos-text, #0f172a)' }}>Add Follow Up Log</h3>
            <button onClick={() => setShowAddFollowUpLogModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: 'var(--etpos-text-muted, #64748b)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
          </div>
          <div style={{ padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: '700', fontSize: '0.875rem', color: 'var(--etpos-text)', marginBottom: '0.4rem' }}>Log Note / Description:*</label>
              <textarea
                value={newLogDescription}
                onChange={(e) => setNewLogDescription(e.target.value)}
                placeholder="Enter log description..."
                style={{ width: '100%', minHeight: '110px', padding: '0.75rem', border: '1px solid var(--etpos-border)', borderRadius: '6px', fontSize: '0.875rem' }}
              />
            </div>
          </div>
          <div style={{ padding: '1rem 1.75rem', background: 'var(--etpos-bg, #f8fafc)', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button
              onClick={async () => {
                if (!newLogDescription.trim()) {
                  toast.warning('Log description is required.');
                  return;
                }
                try {
                  const nowFormatted = formatDatetimeForBusinessUfDate(new Date());
                  await apiService.createCrmFollowUpLog({
                    schedule_id: activeScheduleId,
                    description: newLogDescription,
                    log_type: 'call',
                    start_datetime: nowFormatted,
                    end_datetime: nowFormatted
                  });
                  toast.success('Follow up log added successfully!');
                  setShowAddFollowUpLogModal(false);
                  setNewLogDescription('');
                } catch (e) {
      /* console.error(e); */
                  toast.error('Failed to add follow up log.');
                }
              }}
              style={{ background: '#6366f1', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.55rem 1.75rem', fontWeight: '600', cursor: 'pointer' }}
            >
              Save Log
            </button>
            <button onClick={() => setShowAddFollowUpLogModal(false)} style={{ background: '#64748b', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.55rem 1.75rem', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  const renderLeadDetailsModal = () => {
    if (!showLeadDetailsModal || !leadDetailsData) return null;
    const l = leadDetailsData;

    return (
      <div className="modal-overlay" onClick={() => setShowLeadDetailsModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
        <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--etpos-card, #ffffff)', borderRadius: '12px', width: '750px', maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '1.25rem 1.75rem', background: 'var(--etpos-bg, #f8fafc)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: 'var(--etpos-text, #0f172a)' }}>Lead Details - {l.name || l.first_name || 'View Lead'}</h3>
            <button onClick={() => setShowLeadDetailsModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: 'var(--etpos-text-muted, #64748b)', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
          </div>
          <div style={{ padding: '1.75rem 2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', fontSize: '0.9rem' }}>
            <div><strong style={{ color: 'var(--etpos-text-muted)' }}>Contact ID:</strong> <span style={{ fontWeight: '600', color: 'var(--etpos-text)' }}>{l.contact_id || '-'}</span></div>
            <div><strong style={{ color: 'var(--etpos-text-muted)' }}>Name:</strong> <span style={{ fontWeight: '600', color: 'var(--etpos-text)' }}>{l.supplier_business_name ? `${l.supplier_business_name}, ${l.name || ''}` : (l.name || `${l.first_name || ''} ${l.last_name || ''}`)}</span></div>
            <div><strong style={{ color: 'var(--etpos-text-muted)' }}>Mobile:</strong> <span style={{ fontWeight: '600', color: 'var(--etpos-text)' }}>{l.mobile || '-'}</span></div>
            <div><strong style={{ color: 'var(--etpos-text-muted)' }}>Email:</strong> <span style={{ fontWeight: '600', color: 'var(--etpos-text)' }}>{l.email || '-'}</span></div>
            <div><strong style={{ color: 'var(--etpos-text-muted)' }}>Source:</strong> <span style={{ fontWeight: '600', color: 'var(--etpos-text)' }}>{l.source?.name || l.crm_source || '-'}</span></div>
            <div><strong style={{ color: 'var(--etpos-text-muted)' }}>Life Stage:</strong> <span style={{ fontWeight: '600', color: 'var(--etpos-text)' }}>{l.life_stage?.name || l.crm_life_stage || '-'}</span></div>
            <div><strong style={{ color: 'var(--etpos-text-muted)' }}>Tax Number:</strong> <span style={{ fontWeight: '600', color: 'var(--etpos-text)' }}>{l.tax_number || '-'}</span></div>
            <div><strong style={{ color: 'var(--etpos-text-muted)' }}>Assigned to:</strong> <span style={{ fontWeight: '600', color: 'var(--etpos-text)' }}>{Array.isArray(l.lead_users) ? l.lead_users.map(u => u.first_name || u.name || u.user_full_name || '').filter(Boolean).join(', ') || '-' : (l.lead_users?.first_name || l.lead_users?.name || '-')}</span></div>
            <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: 'var(--etpos-text-muted)' }}>Address:</strong> <span style={{ fontWeight: '600', color: 'var(--etpos-text)' }}>{[l.address_line_1, l.address_line_2, l.city, l.state, l.country, l.zip_code].filter(Boolean).join(', ') || '-'}</span></div>
          </div>
          <div style={{ padding: '1rem 1.75rem', background: 'var(--etpos-bg, #f8fafc)', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowLeadDetailsModal(false)} style={{ background: '#64748b', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '0.55rem 1.75rem', fontWeight: '600', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const crmTabsNav = [
    { id: 'crm', label: 'CRM Overview', icon: 'fas fa-chart-pie' },
    { id: 'leads', label: 'Leads', icon: 'fas fa-user-plus' },
    { id: 'followups', label: 'Follow ups', icon: 'fas fa-calendar-check' },
    { id: 'campaigns', label: 'Campaigns', icon: 'fas fa-bullhorn' },
    { id: 'contacts_login', label: 'Contacts Login', icon: 'fas fa-user-lock' },
    { id: 'commissions', label: 'Commissions', icon: 'fas fa-percentage' },
    { id: 'reports', label: 'Reports', icon: 'fas fa-chart-bar' },
    { id: 'proposal_template', label: 'Proposal Template', icon: 'fas fa-file-invoice' },
    { id: 'proposals', label: 'Proposals', icon: 'fas fa-file-signature' },
    { id: 'order_requests', label: 'Order Requests', icon: 'fas fa-shopping-cart' },
    { id: 'b2b_marketplace', label: 'B2B Marketplace', icon: 'fas fa-store' },
    { id: 'sources', label: 'Sources', icon: 'fas fa-tags' },
    { id: 'life_stage', label: 'Life Stage', icon: 'fas fa-heart' },
    { id: 'followup_category', label: 'Followup Category', icon: 'fas fa-folder' },
    { id: 'settings', label: 'Settings', icon: 'fas fa-sliders' },
  ];

  const renderMfgCrmTabs = () => (
    <div className="mfg-tabs-nav">
      {crmTabsNav.map(t => (
        <button
          key={t.id}
          className={`mfg-tab-btn ${activeTab === t.id ? 'active' : ''}`}
          onClick={() => setActiveTab(t.id)}
        >
          <i className={t.icon} />
          {t.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="mfg-page">
      {/* Page Header Banner */}
      <div className="mfg-page-header">
        <div className="mfg-page-header-title-wrap">
          <div className="mfg-page-icon">
            <i className="fas fa-handshake" />
          </div>
          <div>
            <h1>Customer Relationship Management</h1>
            <div className="mfg-page-header-subtitle">Manage sales leads, customer follow-ups, marketing campaigns, proposals, and contact portals</div>
          </div>
        </div>
      </div>

      {/* Top Stats Overview Grid */}
      <div className="mfg-stats-grid">
        <div className="mfg-stat-card">
          <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(79, 70, 229, 0.1)', color: '#4f46e5' }}>
            <i className="fas fa-calendar-check" />
          </div>
          <div className="mfg-stat-info">
            <div className="mfg-stat-label">Today's Follow ups</div>
            <div className="mfg-stat-value">{crmDashboardData?.todays_followups || 0}</div>
            <div className="mfg-stat-subtext">Scheduled for today</div>
          </div>
        </div>

        <div className="mfg-stat-card">
          <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}>
            <i className="fas fa-user-plus" />
          </div>
          <div className="mfg-stat-info">
            <div className="mfg-stat-label">My Leads</div>
            <div className="mfg-stat-value">{crmDashboardData?.my_leads || leadsData.length || 0}</div>
            <div className="mfg-stat-subtext">Active prospect leads</div>
          </div>
        </div>

        <div className="mfg-stat-card">
          <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
            <i className="fas fa-users" />
          </div>
          <div className="mfg-stat-info">
            <div className="mfg-stat-label">Total Customers</div>
            <div className="mfg-stat-value">{crmDashboardData?.total_customers || 47}</div>
            <div className="mfg-stat-subtext">Converted customer database</div>
          </div>
        </div>

        <div className="mfg-stat-card">
          <div className="mfg-stat-icon-wrap" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}>
            <i className="fas fa-bullhorn" />
          </div>
          <div className="mfg-stat-info">
            <div className="mfg-stat-label">Campaigns</div>
            <div className="mfg-stat-value">{campaignsData.length || 0}</div>
            <div className="mfg-stat-subtext">Marketing campaigns active</div>
          </div>
        </div>
      </div>

      {/* Top Navigation Pill Tabs Bar */}
      {renderMfgCrmTabs()}

      {/* Main Content Area */}
      <div className="crm-content" style={{ marginTop: '1rem' }}>
        {activeTab === 'crm' && (
          <div className="mfg-card">
            <div className="mfg-card-title">
              <i className="fas fa-chart-pie" style={{ color: '#4f46e5' }} /> CRM Dashboard Overview
            </div>

            {/* Quick Metrics */}
            <div className="crm-grid-3">
              {[
                { label: "Today's Follow ups", value: crmDashboardData?.todays_followups || 0, icon: <Calendar size={22} color="#6366f1" />, bg: '#ede9fe' },
                { label: 'My Leads', value: crmDashboardData?.my_leads || 0, icon: <TrendingUp size={22} color="#3b82f6" />, bg: '#dbeafe' },
                { label: 'Conversion Rate', value: `${crmDashboardData?.my_conversion || 0}%`, icon: <UserCheck size={22} color="#10b981" />, bg: '#dcfce7' }
              ].map((card, i) => (
                <div key={i} className="crm-info-box">
                  <div className="crm-info-box-icon" style={{ background: card.bg }}>{card.icon}</div>
                  <div className="crm-info-box-content">
                    <p className="crm-info-box-text">{card.label}</p>
                    <p className="crm-info-box-number">{card.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* My Follow ups + My Call Logs */}
            <div className="crm-grid-2">
              <div className="crm-widget">
                <div className="crm-widget-header">My Follow ups</div>
                <div className="crm-widget-body">
                  <table className="mfg-table">
                    <thead>
                      <tr>
                        {['Status', 'Total'].map((h, i) => <th key={i}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {['scheduled', 'open', 'cancelled', 'completed'].map(status => (
                        <tr key={status}>
                          <td style={{ textTransform: 'capitalize', fontWeight: 600 }}>{crmDashboardData?.statuses?.[status] || status}</td>
                          <td>{crmDashboardData?.my_follow_ups_arr?.[status] || 0}</td>
                        </tr>
                      ))}
                      <tr>
                        <td><strong>Total</strong></td>
                        <td><strong>{['scheduled', 'open', 'cancelled', 'completed'].reduce((sum, s) => sum + (crmDashboardData?.my_follow_ups_arr?.[s] || 0), 0)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="crm-widget">
                <div className="crm-widget-header">My Call Logs</div>
                <div className="crm-widget-body">
                  <table className="mfg-table">
                    <thead>
                      <tr><th>Metric</th><th>Count</th></tr>
                    </thead>
                    <tbody>
                      <tr><td style={{ fontWeight: 600 }}>Calls today</td><td>{crmDashboardData?.my_call_logs?.calls_today || 0}</td></tr>
                      <tr><td style={{ fontWeight: 600 }}>Calls yesterday</td><td>{crmDashboardData?.my_call_logs?.calls_yesterday || 0}</td></tr>
                      <tr><td style={{ fontWeight: 600 }}>Calls this month</td><td>{crmDashboardData?.my_call_logs?.calls_this_month || 0}</td></tr>
                      <tr><td><strong>Total</strong></td><td><strong>{(crmDashboardData?.my_call_logs?.calls_today || 0) + (crmDashboardData?.my_call_logs?.calls_yesterday || 0) + (crmDashboardData?.my_call_logs?.calls_this_month || 0)}</strong></td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Sources & Life Stages breakdown */}
            <div className="crm-grid-2">
              <div className="crm-widget">
                <div className="crm-widget-header">Sources Breakdown</div>
                <div className="crm-widget-body">
                  <table className="mfg-table">
                    <thead><tr><th>Source</th><th>Leads</th><th>Customers</th></tr></thead>
                    <tbody>
                      {(crmDashboardData?.sources || []).length === 0 ? (
                        <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--etpos-text-muted, #64748b)' }}>No data available</td></tr>
                      ) : (
                        crmDashboardData.sources.map((s, i) => (
                          <tr key={s.id || i}>
                            <td style={{ fontWeight: 600 }}>{s.name || '-'}</td>
                            <td>{crmDashboardData?.leads_count_by_source?.[s.id] || 0}</td>
                            <td>{crmDashboardData?.customers_count_by_source?.[s.id] || 0}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="crm-widget">
                <div className="crm-widget-header">Life Stages Breakdown</div>
                <div className="crm-widget-body">
                  <table className="mfg-table">
                    <thead><tr><th>Life Stage</th><th>Leads</th></tr></thead>
                    <tbody>
                      {(crmDashboardData?.life_stages || []).length === 0 ? (
                        <tr><td colSpan={2} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--etpos-text-muted, #64748b)' }}>No data available</td></tr>
                      ) : (
                        crmDashboardData.life_stages.map((s, i) => (
                          <tr key={s.id || i}>
                            <td style={{ fontWeight: 600 }}>{s.name || '-'}</td>
                            <td>{crmDashboardData?.leads_by_life_stage?.[s.id]?.length || 0}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Birthdays */}
            <div className="crm-grid-2">
              <div className="crm-widget">
                <div className="crm-widget-header">Today's Birthdays</div>
                <div className="crm-widget-body">
                  <table className="mfg-table">
                    <thead><tr><th>Name</th></tr></thead>
                    <tbody>
                      {(crmDashboardData?.todays_birthdays || []).length === 0 ? (
                        <tr><td style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--etpos-text-muted, #64748b)' }}>No birthdays today</td></tr>
                      ) : (
                        crmDashboardData.todays_birthdays.map((b, i) => (
                          <tr key={i}><td style={{ fontWeight: 600 }}>{b.name || '-'}</td></tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="crm-widget">
                <div className="crm-widget-header">Upcoming Birthdays</div>
                <div className="crm-widget-body">
                  <table className="mfg-table">
                    <thead><tr><th>Name</th><th>DOB</th></tr></thead>
                    <tbody>
                      {(crmDashboardData?.upcoming_birthdays || []).length === 0 ? (
                        <tr><td colSpan={2} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--etpos-text-muted, #64748b)' }}>No upcoming birthdays</td></tr>
                      ) : (
                        crmDashboardData.upcoming_birthdays.map((b, i) => (
                          <tr key={i}><td style={{ fontWeight: 600 }}>{b.name || '-'}</td><td>{b.dob || '-'}</td></tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'leads' && (
          <div className="mfg-card">
            <div className="mfg-card-title">
              <i className="fas fa-user-plus" style={{ color: '#4f46e5' }} /> All Leads Management
            </div>
            {(() => {
              const filteredLeads = leadsData.filter(lead => {
                const searchLower = leadsSearch.toLowerCase();
                const matchesSearch = !leadsSearch || [
                  lead.name, lead.supplier_business_name, lead.mobile, lead.email, lead.contact_id, lead.address
                ].some(val => String(val || '').toLowerCase().includes(searchLower));

                const matchesSource = !leadsSourceFilter || String(lead.crm_source || lead.source?.id || '') === String(leadsSourceFilter);
                const matchesLifeStage = !leadsLifeStageFilter || String(lead.crm_life_stage || lead.life_stage?.id || '') === String(leadsLifeStageFilter);
                const matchesAssigned = !leadsAssignedToFilter || (
                  Array.isArray(lead.lead_users) ? lead.lead_users.some(u => String(u.id || u.user_id || u) === String(leadsAssignedToFilter)) : String(lead.assigned_to || '') === String(leadsAssignedToFilter)
                );

                return matchesSearch && matchesSource && matchesLifeStage && matchesAssigned;
              });

              return (
                <>
                  {/* Table Controls Toolbar */}
                  <div className="table-controls" style={{ marginBottom: '1rem' }}>
                    <div className="table-controls-left" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div className="entries-dropdown"><span className="entries-label">Show</span><select value={leadsEntries} onChange={e => setLeadsEntries(e.target.value)}><option>25</option></select><span className="entries-label">entries</span></div>
                      <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                        <button onClick={() => setLeadViewMode('list')} style={{ padding: '0.35rem 0.75rem', background: leadViewMode === 'list' ? '#6366f1' : 'var(--etpos-bg)', color: leadViewMode === 'list' ? '#fff' : 'var(--etpos-text)', border: '1px solid var(--etpos-border)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>List View</button>
                        <button onClick={() => setLeadViewMode('kanban')} style={{ padding: '0.35rem 0.75rem', background: leadViewMode === 'kanban' ? '#6366f1' : 'var(--etpos-bg)', color: leadViewMode === 'kanban' ? '#fff' : 'var(--etpos-text)', border: '1px solid var(--etpos-border)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}>Kanban Board</button>
                      </div>
                    </div>
                    <div className="table-controls-right" style={{ display: 'flex', gap: '0.5rem' }}>
                      <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input type="text" value={leadsSearch} onChange={e => setLeadsSearch(e.target.value)} placeholder="Search..." className="tabs-search-input" />
                        <button onClick={() => setShowLeadsFilters(true)} style={{ padding: '0.45rem 0.85rem', background: 'var(--etpos-card, #ffffff)', border: '1px solid var(--etpos-border, #cbd5e1)', borderRadius: '4px', fontSize: '0.85rem', fontWeight: '600', color: '#2563eb', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg> Filters
                        </button>
                      </div>
                      <ExportDropdown show={showLeadsExportDropdown} setShow={setShowLeadsExportDropdown} />
                      <button onClick={() => { fetchCrmCreateResources(); setShowAddLeadModal(true); }} className="control-btn-primary">+ Add</button>
                    </div>
                  </div>
                    {leadViewMode === 'kanban' ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                        {(crmResources.lifeStages.length ? crmResources.lifeStages : [{ id: 'new', name: 'New' }, { id: 'contacted', name: 'Contacted' }, { id: 'qualified', name: 'Qualified' }]).map(stage => {
                          const stageLeads = filteredLeads.filter(l => String(l.life_stage?.id || l.crm_life_stage || '').toLowerCase() === String(stage.id || stage.name || '').toLowerCase());
                          return (
                            <div key={stage.id || stage.name} style={{ background: 'var(--etpos-card)', border: '1px solid var(--etpos-border)', borderRadius: '8px', padding: '1rem' }}>
                              <h5 style={{ margin: '0 0 0.75rem', fontWeight: '700', fontSize: '0.9rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{stage.name}</span>
                                <span style={{ background: '#e0e7ff', color: '#4338ca', borderRadius: '12px', padding: '0.1rem 0.55rem', fontSize: '0.75rem' }}>{stageLeads.length}</span>
                              </h5>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {stageLeads.length === 0 ? (
                                  <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--etpos-text-muted)', fontSize: '0.8rem', border: '1px dashed var(--etpos-border)', borderRadius: '6px' }}>No leads in this stage</div>
                                ) : (
                                  stageLeads.map(lead => (
                                    <div key={lead.id} style={{ background: 'var(--etpos-bg)', border: '1px solid var(--etpos-border)', borderRadius: '6px', padding: '0.75rem' }}>
                                      <div style={{ fontWeight: '700', fontSize: '0.875rem', marginBottom: '0.25rem', color: 'var(--etpos-text)' }}>{lead.name}</div>
                                      <div style={{ fontSize: '0.8rem', color: 'var(--etpos-text-muted)', marginBottom: '0.5rem' }}>{lead.mobile || lead.email || '-'}</div>
                                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                                        <button onClick={() => handleConvertLead(lead.id)} style={{ padding: '0.2rem 0.45rem', background: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '600' }}>Convert</button>
                                        <button onClick={() => handleDeleteLead(lead.id)} style={{ padding: '0.2rem 0.45rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '600' }}>Delete</button>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto', border: '1px solid var(--etpos-border)', borderRadius: '8px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                          <thead>
                            <tr style={{ background: 'var(--etpos-bg)', borderBottom: '2px solid var(--etpos-border)' }}>
                              {['Action', 'Contact ID', 'Name', 'Mobile', 'Email', 'Source', 'Last follow up', 'Upcoming follow up', 'Life Stage', 'Assigned to', 'Address', 'Tax number'].map((h, i) => (<th key={i} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '700', whiteSpace: 'nowrap' }}>{h}</th>))}
                            </tr>
                          </thead>
                          <tbody>
                            {crmLoading ? (
                              <tr><td colSpan={12} style={{ padding: '2.5rem', textAlign: 'center' }}>Loading...</td></tr>
                            ) : filteredLeads.length === 0 ? (
                              <tr><td colSpan={12} style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--etpos-text-muted)' }}>No data available in table</td></tr>
                            ) : (
                              filteredLeads.map((lead, i) => (
                                <tr key={lead.id || i} style={{ borderBottom: '1px solid var(--etpos-border)' }}>
                                  <td style={{ padding: '0.75rem 1rem', position: 'relative' }}>
                                    <div style={{ position: 'relative' }}>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenLeadActionId(openLeadActionId === lead.id ? null : lead.id);
                                        }}
                                        style={{ background: '#0284c7', color: '#ffffff', border: 'none', borderRadius: '15px', padding: '0.35rem 0.85rem', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                                      >
                                        Action <ChevronDown size={13} />
                                      </button>
                                      {openLeadActionId === lead.id && (
                                        <div style={{ position: 'absolute', top: '100%', left: 0, background: 'var(--etpos-card, #ffffff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100, minWidth: '175px', padding: '0.35rem 0' }}>
                                          <button onClick={() => handleViewLeadDetails(lead)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.45rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.825rem', color: 'var(--etpos-text, #0f172a)', cursor: 'pointer' }}>
                                            👁 View
                                          </button>
                                          <button onClick={() => handleEditLead(lead)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.45rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.825rem', color: 'var(--etpos-text, #0f172a)', cursor: 'pointer' }}>
                                            ✏️ Edit
                                          </button>
                                          <button onClick={() => { setOpenLeadActionId(null); handleConvertLead(lead); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.45rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.825rem', color: 'var(--etpos-text, #0f172a)', cursor: 'pointer' }}>
                                            🔄 Convert to customer
                                          </button>
                                          <button onClick={() => { setOpenLeadActionId(null); handleDeleteLead(lead); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.45rem 1rem', border: 'none', background: 'none', textAlign: 'left', fontSize: '0.825rem', color: '#ef4444', cursor: 'pointer' }}>
                                            🗑 Delete
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>{lead.contact_id || '-'}</td>
                                  <td style={{ padding: '0.75rem 1rem' }}>
                                    {lead.supplier_business_name ? `${lead.supplier_business_name}, ${lead.name || ''}` : (lead.name || '-')}
                                  </td>
                                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>{lead.mobile || '-'}</td>
                                  <td style={{ padding: '0.75rem 1rem' }}>{lead.email || '-'}</td>
                                  <td style={{ padding: '0.75rem 1rem' }}>{lead.source?.name || lead.crm_source || '-'}</td>
                                  <td style={{ padding: '0.75rem 1rem' }}>
                                    {String(lead.last_follow_up || '').replace(/<[^>]*>?/gm, '').replace(/Add Follow Up|Add Schedule|\+/gi, '').trim() || '-'}
                                  </td>
                                  <td style={{ padding: '0.75rem 1rem' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', alignItems: 'flex-start' }}>
                                      {(() => {
                                        const cleanUpcoming = String(lead.upcoming_follow_up || '').replace(/<[^>]*>?/gm, '').replace(/Add Follow Up|Add Schedule|\+/gi, '').trim();
                                        return cleanUpcoming ? <span>{cleanUpcoming}</span> : null;
                                      })()}
                                      <button
                                        onClick={() => {
                                          setFollowupForm(prev => ({ ...prev, customer: lead.id }));
                                          setShowAddFollowupModal(true);
                                        }}
                                        style={{ background: 'none', color: '#6366f1', border: '1px solid #6366f1', borderRadius: '15px', padding: '0.25rem 0.65rem', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                      >
                                        + Add Follow Up
                                      </button>
                                    </div>
                                  </td>
                                  <td style={{ padding: '0.75rem 1rem' }}>{lead.life_stage?.name || lead.crm_life_stage || '-'}</td>
                                  <td style={{ padding: '0.75rem 1rem' }}>
                                    <div style={{ display: 'flex', gap: '0.2rem' }}>
                                      {Array.isArray(lead.lead_users) && lead.lead_users.length > 0 ? (
                                        lead.lead_users.map((u, idx) => {
                                          const initial = (u.first_name || u.name || u.surname || 'U').charAt(0).toUpperCase();
                                          return (
                                            <span key={idx} title={u.first_name || u.name} style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#64748b', color: '#ffffff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '700' }}>
                                              {initial}
                                            </span>
                                          );
                                        })
                                      ) : '-'}
                                    </div>
                                  </td>
                                  <td style={{ padding: '0.75rem 1rem' }}>
                                    {lead.address || [lead.address_line_1, lead.city, lead.state, lead.country].filter(Boolean).join(', ') || '-'}
                                  </td>
                                  <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>{lead.tax_number || '-'}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {activeTab === 'followups' && renderFollowupsTab()}
          {activeTab === 'campaigns' && renderCampaignsTab()}
          {activeTab === 'contacts_login' && renderContactsLoginTab()}
          {activeTab === 'commissions' && renderCommissionsTab()}
          {activeTab === 'reports' && renderReportsTab()}
          {activeTab === 'proposal_template' && renderProposalTemplateTab()}
          {activeTab === 'proposals' && renderProposalsTab()}
          {activeTab === 'order_requests' && renderOrderRequestsTab()}
          {activeTab === 'b2b_marketplace' && renderMarketplaceTab()}
          {activeTab === 'call_logs' && renderCallLogsTab()}
          {activeTab === 'sources' && renderSourcesTab()}
          {activeTab === 'life_stage' && renderLifeStageTab()}
          {activeTab === 'followup_category' && renderFollowupCategoryTab()}
          {activeTab === 'settings' && renderSettingsTab()}
        </div>
        {showAddLeadModal && renderAddLeadModal()}
        {showAddFollowupModal && renderAddFollowupModal()}
        {showAddCampaignModal && renderAddCampaignModal()}
        {showFollowupFilters && renderFollowupFiltersModal()}
        {showLeadsFilters && renderLeadsFiltersModal()}
        {showAddContactLoginModal && renderAddContactLoginModal()}
        {showContactLoginFilters && renderContactLoginFiltersModal()}
        {showCommissionsFilters && renderCommissionsFiltersModal()}
        {showAddProposalTemplateModal && renderAddProposalTemplateModal()}
        {showSendProposalModal && renderSendProposalModal()}
        {showAddSourceModal && renderAddSourceModal()}
        {showAddLifeStageModal && renderAddLifeStageModal()}
        {showAddFollowupCategoryModal && renderAddFollowupCategoryModal()}
        {showScheduleLogsModal && renderScheduleLogsModal()}
        {showReportFollowupsModal && renderReportFollowupsModal()}
        {showLeadDetailsModal && renderLeadDetailsModal()}
        {showFollowUpLogModal && renderFollowUpLogModal()}
        {showAddFollowUpLogModal && renderAddFollowUpLogModal()}
    </div>
  );
};

export default CRM;
